import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import PasswordConfirmModal from '@/components/PasswordConfirmModal';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LogOut, Users, Building2, TrendingUp, Wallet, Search, Download, ArrowLeft, Briefcase, FileText, FileSpreadsheet, Clock, AlertTriangle, CheckCircle, Calculator, CreditCard, Plus, Pencil, UserPlus, ArrowDownUp, ArrowUpFromLine, ImageIcon, Trash2, Home, FileDown, Share2, Copy, Check, Menu, CalendarDays } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (value) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(value);
const formatPercent = (value) => `${(value * 100).toFixed(2)}%`;
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  if (!isNaN(dateStr) && Number(dateStr) > 10000) {
    const excelEpoch = new Date(1899, 11, 30);
    return new Date(excelEpoch.getTime() + Number(dateStr) * 86400000).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  }
  try { return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return dateStr; }
};

const CHART_COLORS = ['#1B4D3E', '#D4AF37', '#3D7A5A', '#E8C547', '#5A9A7A', '#F0D978', '#7BB99A', '#F5E6A3', '#2E6B55', '#C9A227'];

// Calculate maturity status and days remaining
const getMaturityStatus = (maturityDateStr) => {
  if (!maturityDateStr) return { status: 'unknown', daysRemaining: null, label: 'N/A' };
  
  let maturityDate;
  if (!isNaN(maturityDateStr) && Number(maturityDateStr) > 10000) {
    const excelEpoch = new Date(1899, 11, 30);
    maturityDate = new Date(excelEpoch.getTime() + Number(maturityDateStr) * 86400000);
  } else {
    maturityDate = new Date(maturityDateStr);
  }
  
  if (isNaN(maturityDate.getTime())) return { status: 'unknown', daysRemaining: null, label: 'N/A' };
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  maturityDate.setHours(0, 0, 0, 0);
  
  const diffTime = maturityDate - today;
  const daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  const formattedDate = maturityDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  
  if (daysRemaining < 0) return { status: 'matured', daysRemaining, label: formattedDate, tooltip: 'Already matured' };
  else if (daysRemaining < 30) return { status: 'critical', daysRemaining, label: formattedDate, tooltip: `Matures in ${daysRemaining} days` };
  else if (daysRemaining < 60) return { status: 'warning', daysRemaining, label: formattedDate, tooltip: `Matures in ${daysRemaining} days` };
  else if (daysRemaining < 90) return { status: 'upcoming', daysRemaining, label: formattedDate, tooltip: `Matures in ${daysRemaining} days` };
  else return { status: 'safe', daysRemaining, label: formattedDate, tooltip: `Matures in ${daysRemaining} days` };
};

// Maturity Badge Component
const MaturityBadge = ({ maturityDate, maturityPct1, maturityDate2, maturityPct2, maturityDate3, maturityPct3 }) => {
  const hasMulti = maturityPct2 || maturityPct3;
  if (hasMulti) {
    const parts = [];
    const fmtDt = (d) => { try { const dt = new Date(d); return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return d; } };
    if (maturityDate) parts.push(`${maturityPct1 || 100}% - ${fmtDt(maturityDate)}`);
    if (maturityPct2 && maturityDate2) parts.push(`${maturityPct2}% - ${fmtDt(maturityDate2)}`);
    if (maturityPct3 && maturityDate3) parts.push(`${maturityPct3}% - ${fmtDt(maturityDate3)}`);
    return <span className="text-xs leading-relaxed">{parts.join(', ')}</span>;
  }
  const { status, label, tooltip, daysRemaining } = getMaturityStatus(maturityDate);
  const statusStyles = {
    matured: { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-300', icon: CheckCircle },
    critical: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', icon: AlertTriangle },
    warning: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', icon: Clock },
    upcoming: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200', icon: Clock },
    safe: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', icon: CheckCircle },
    unknown: { bg: 'bg-gray-50', text: 'text-gray-500', border: 'border-gray-200', icon: Clock }
  };
  const style = statusStyles[status];
  const Icon = style.icon;
  
  return (
    <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${style.bg} ${style.text} border ${style.border}`} title={tooltip}>
      <Icon className="h-3 w-3" />
      <span>{label}</span>
      {status === 'critical' && daysRemaining !== null && <span className="ml-1 px-1.5 py-0.5 bg-red-100 rounded text-[10px] font-bold">{daysRemaining}d</span>}
      {status === 'warning' && daysRemaining !== null && <span className="ml-1 px-1.5 py-0.5 bg-orange-100 rounded text-[10px] font-bold">{daysRemaining}d</span>}
    </div>
  );
};

const PartnerDashboard = () => {
  const { user, logout, getAuthHeaders, branding, colorConfig } = useAuth();
  const [activeTab, setActiveTab] = useState('clients');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [clients, setClients] = useState([]);
  // Password-confirmed delete pipeline
  const [pwdConfirm, setPwdConfirm] = useState({ open: false, title: '', description: '', itemLabel: '', onConfirmed: null });
  const requestDelete = useCallback(({ title = 'Confirm Delete', description, itemLabel, onConfirmed }) => {
    setPwdConfirm({ open: true, title, description: description || 'This action cannot be undone.', itemLabel, onConfirmed });
  }, []);
  const [summary, setSummary] = useState({ total_clients: 0, total_invested_value: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientHoldings, setClientHoldings] = useState([]);
  const [clientRedeemed, setClientRedeemed] = useState([]);
  const [clientSummary, setClientSummary] = useState(null);
  const [showTransactions, setShowTransactions] = useState(false);
  const [selectedHolding, setSelectedHolding] = useState(null);
  const [transactions, setTransactions] = useState({ investments: [], redemptions: [] });

  // Commission state
  const [commissionMonth, setCommissionMonth] = useState(new Date().getMonth() + 1);
  const [commissionYear, setCommissionYear] = useState(new Date().getFullYear());
  const [commissionRows, setCommissionRows] = useState([]);
  const [commissionTotal, setCommissionTotal] = useState(0);
  const [commissionMeta, setCommissionMeta] = useState(null);
  const [loadingCommission, setLoadingCommission] = useState(false);

  // Payout state
  const [payoutRows, setPayoutRows] = useState([]);
  const [loadingPayout, setLoadingPayout] = useState(false);

  // SubBroker Commission state
  const [sbCommMonth, setSbCommMonth] = useState(new Date().getMonth() + 1);
  const [sbCommYear, setSbCommYear] = useState(new Date().getFullYear());
  const [sbCommRows, setSbCommRows] = useState([]);
  const [sbCommTotal, setSbCommTotal] = useState(0);
  const [sbCommMeta, setSbCommMeta] = useState(null);
  const [loadingSbComm, setLoadingSbComm] = useState(false);

  // SubBroker Payout state
  const [sbPayoutMonth, setSbPayoutMonth] = useState(new Date().getMonth() + 1);
  const [sbPayoutYear, setSbPayoutYear] = useState(new Date().getFullYear());
  const [sbPayoutRows, setSbPayoutRows] = useState([]);
  const [loadingSbPayout, setLoadingSbPayout] = useState(false);

  // My Bond Commission state
  const [mbCommMonth, setMbCommMonth] = useState(new Date().getMonth() + 1);
  const [mbCommYear, setMbCommYear] = useState(new Date().getFullYear());
  const [mbCommRows, setMbCommRows] = useState([]);
  const [mbCommTotal, setMbCommTotal] = useState(0);
  const [mbCommMeta, setMbCommMeta] = useState(null);
  const [loadingMbComm, setLoadingMbComm] = useState(false);

  // My Bond Payout state
  const [mbPayoutRows, setMbPayoutRows] = useState([]);
  const [loadingMbPayout, setLoadingMbPayout] = useState(false);

  // Add Client state
  const [showAddClient, setShowAddClient] = useState(false);
  const [newClient, setNewClient] = useState({ client_name: '', email: '', mobile: '', subpartner_code: '' });
  const [savingClient, setSavingClient] = useState(false);

  // Edit Client state
  const [showEditClient, setShowEditClient] = useState(false);
  const [editClient, setEditClient] = useState({ client_id: '', client_name: '', email: '', mobile: '', hasInvestments: false, subpartner_code: '' });

  // User Logins state
  const [userLogins, setUserLogins] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', password: '', entity_id: '', role: 'client' });
  const [showEditUser, setShowEditUser] = useState(false);
  const [editUser, setEditUser] = useState({ id: '', username: '', password: '', entity_name: '', is_self: false });
  const [savingUser, setSavingUser] = useState(false);

  // Investments & Redemptions state
  const [partnerInvestments, setPartnerInvestments] = useState([]);
  const [loadingInvestments, setLoadingInvestments] = useState(false);
  const [partnerRedemptions, setPartnerRedemptions] = useState([]);
  const [loadingRedemptions, setLoadingRedemptions] = useState(false);

  // Curated Bonds state
  const [curatedBonds, setCuratedBonds] = useState([]);
  const [loadingCurated, setLoadingCurated] = useState(false);
  const [showBondImagePreview, setShowBondImagePreview] = useState(false);
  const [previewBondImage, setPreviewBondImage] = useState('');
  const [showEnquiryDialog, setShowEnquiryDialog] = useState(false);
  const [enquiryBond, setEnquiryBond] = useState(null);
  const [enquiryAmount, setEnquiryAmount] = useState('');
  const [sendingEnquiry, setSendingEnquiry] = useState(false);
  const [partnerMobileNumber, setPartnerMobileNumber] = useState(null);

  // Redeem Request state
  const [showRedeemDialog, setShowRedeemDialog] = useState(false);
  const [redeemBond, setRedeemBond] = useState(null);
  const [redeemQty, setRedeemQty] = useState('');
  const [sendingRedeem, setSendingRedeem] = useState(false);

  // Partner Config state
  const [partnerConfig, setPartnerConfig] = useState({ allow_add_investment_redemption: false, allow_subpartner: false, hide_commission_from_subpartners: false });
  const [linkCopied, setLinkCopied] = useState(false);
  const [showInvModal, setShowInvModal] = useState(false);
  const [editingInv, setEditingInv] = useState(null);
  const [invForm, setInvForm] = useState({ transaction_date: '', bond_isin: '', bond_name: '', quantity: '', face_value: '', invested_value: '', yield_rate: '', maturity_date: '', maturity_pct_1: '100', maturity_date_2: '', maturity_pct_2: '', maturity_date_3: '', maturity_pct_3: '', client_name: '', partner_share: '', commn_type: 'Trail' });
  const [savingInv, setSavingInv] = useState(false);
  const [showRedModal, setShowRedModal] = useState(false);
  const [editingRed, setEditingRed] = useState(null);
  const [redForm, setRedForm] = useState({ investment_txn_id: '', transaction_date: '', quantity: '', redemption_value: '' });
  const [redClientId, setRedClientId] = useState('');
  const [savingRed, setSavingRed] = useState(false);
  const [investmentOptions, setInvestmentOptions] = useState([]);

  // Maturity Report state
  const [maturityFilter, setMaturityFilter] = useState('3m');
  const [maturityCustomDate, setMaturityCustomDate] = useState('');
  const [maturityReport, setMaturityReport] = useState([]);
  const [loadingMaturity, setLoadingMaturity] = useState(false);
  const [bondHoldingsData, setBondHoldingsData] = useState([]);
  const [bondHoldingsFilter, setBondHoldingsFilter] = useState('__all__');
  const [bondClientBreakdown, setBondClientBreakdown] = useState([]);
  const [loadingBondHoldings, setLoadingBondHoldings] = useState(false);
  const [bhTxnData, setBhTxnData] = useState({ investments: [], redemptions: [] });
  const [bhTxnClient, setBhTxnClient] = useState(null);
  const [showBhTxn, setShowBhTxn] = useState(false);

  // SubPartner state
  const [subpartners, setSubpartners] = useState([]);
  const [loadingSubpartners, setLoadingSubpartners] = useState(false);
  const [showAddSubpartner, setShowAddSubpartner] = useState(false);
  const [showEditSubpartner, setShowEditSubpartner] = useState(false);
  const [subpartnerForm, setSubpartnerForm] = useState({ subpartner_name: '', email: '', mobile: '', subpartner_share: '' });
  const [editingSubpartnerCode, setEditingSubpartnerCode] = useState(null);
  const [savingSubpartner, setSavingSubpartner] = useState(false);
  const [subpartnerClients, setSubpartnerClients] = useState([]);
  const [loadingSpClients, setLoadingSpClients] = useState(false);
  const [spClientFilter, setSpClientFilter] = useState('all');

  // My Bonds state
  const [myBonds, setMyBonds] = useState([]);
  const [loadingMyBonds, setLoadingMyBonds] = useState(false);
  const [showMyBondModal, setShowMyBondModal] = useState(false);
  const [editingMyBond, setEditingMyBond] = useState(null);
  const [myBondForm, setMyBondForm] = useState({ bond_isin: '', bond_name: '', rating: '', yield_rate: '', maturity_date: '', maturity_pct_1: '100', maturity_date_2: '', maturity_pct_2: '', maturity_date_3: '', maturity_pct_3: '', interest_payout_frequency: '', available: true, bond_image_url: '' });
  const [savingMyBond, setSavingMyBond] = useState(false);

  // Family State
  const [families, setFamilies] = useState([]);
  const [showFamilyModal, setShowFamilyModal] = useState(false);
  const [editingFamily, setEditingFamily] = useState(null);
  const [familyForm, setFamilyForm] = useState({ family_name: '', head_client_id: '', member_client_ids: [] });
  const [cmrFile, setCmrFile] = useState(null);

  const monthOptions = [
    { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
    { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
    { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
    { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }
  ];

  // Use partner logo if available, otherwise use BondMaster logo
  const logoUrl = branding?.partner_logo_url || "https://bondmaster.com.in/logoBond.png";
  
  // Dynamic colors from config
  const colors = colorConfig || {
    primary: "#1B4D3E",
    primary_hover: "#163d32",
    accent: "#D4AF37",
    light_bg: "#E8F5E9",
    card_bg: "#F8FAF9",
    text_primary: "#1B4D3E",
    text_secondary: "#64748b"
  };

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      const [clientsRes, summaryRes] = await Promise.all([
        axios.get(`${API_URL}/api/partner/clients`, getAuthHeaders()),
        axios.get(`${API_URL}/api/partner/summary`, getAuthHeaders())
      ]);
      setClients(clientsRes.data);
      setSummary(summaryRes.data);
    } catch (error) { toast.error('Failed to load clients'); }
    finally { setLoading(false); }
  }, [getAuthHeaders]);

  const fetchPartnerConfig = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/partner/config`, getAuthHeaders());
      setPartnerConfig(response.data || { allow_add_investment_redemption: false });
    } catch (error) { toast.error('Failed to load partner config'); }
  }, [getAuthHeaders]);

  useEffect(() => { fetchClients(); fetchPartnerConfig(); }, [fetchClients, fetchPartnerConfig]);

  // Set partner favicon and title if available
  useEffect(() => {
    const faviconUrl = branding?.partner_favicon_url;
    if (faviconUrl) {
      const name = branding?.partner_name || 'BondMaster';
      document.title = `${name} | ${user?.role === 'subpartner' ? 'SubPartner' : 'Partner'} Dashboard`;
      const setLink = (rel, href, sizes) => {
        let sel = `link[rel="${rel}"]`;
        if (sizes) sel += `[sizes="${sizes}"]`;
        let el = document.querySelector(sel);
        if (!el) { el = document.createElement('link'); el.setAttribute('rel', rel); if (sizes) el.setAttribute('sizes', sizes); document.head.appendChild(el); }
        el.setAttribute('href', href);
      };
      setLink('icon', faviconUrl);
      setLink('shortcut icon', faviconUrl);
      setLink('apple-touch-icon', faviconUrl);
      setLink('apple-touch-icon', faviconUrl, '180x180');
    }
    return () => {
      document.title = 'BondMaster | Bond Portfolio Management Platform';
      const def = 'https://bondmaster.com.in/logoBond.png';
      ['icon', 'shortcut icon', 'apple-touch-icon'].forEach(rel => {
        const el = document.querySelector(`link[rel="${rel}"]`);
        if (el) el.setAttribute('href', def);
      });
    };
  }, [branding?.partner_favicon_url, branding?.partner_name, user?.role]);

  // For subpartner, fetch the parent partner's mobile for WhatsApp enquiry
  useEffect(() => {
    if (user?.role === 'subpartner') {
      axios.get(`${API_URL}/api/client/partner-mobile`, getAuthHeaders())
        .then(res => setPartnerMobileNumber(res.data?.mobile || null))
        .catch(() => setPartnerMobileNumber(null));
    }
  }, [user?.role, getAuthHeaders]);

  // SubPartner fetch functions
  const fetchSubpartners = useCallback(async () => {
    if (user?.role !== 'partner') return;
    try {
      setLoadingSubpartners(true);
      const response = await axios.get(`${API_URL}/api/partner/subpartners`, getAuthHeaders());
      setSubpartners(response.data || []);
    } catch (error) { toast.error('Failed to load subpartners'); }
    finally { setLoadingSubpartners(false); }
  }, [getAuthHeaders, user?.role]);

  const fetchSubpartnerClients = useCallback(async () => {
    if (user?.role !== 'partner') return;
    try {
      setLoadingSpClients(true);
      const response = await axios.get(`${API_URL}/api/partner/subpartner-clients`, getAuthHeaders());
      setSubpartnerClients(response.data || []);
    } catch (error) { toast.error('Failed to load subpartner clients'); }
    finally { setLoadingSpClients(false); }
  }, [getAuthHeaders, user?.role]);

  const openAddSubpartner = () => {
    setSubpartnerForm({ subpartner_name: '', email: '', mobile: '', subpartner_share: '' });
    setEditingSubpartnerCode(null);
    setShowAddSubpartner(true);
  };

  const openEditSubpartner = (sp) => {
    setSubpartnerForm({ subpartner_name: sp.subpartner_name, email: sp.email || '', mobile: sp.mobile || '', subpartner_share: sp.subpartner_share || '' });
    setEditingSubpartnerCode(sp.subpartner_code);
    setShowEditSubpartner(true);
  };

  const handleSaveSubpartner = async (isEdit = false) => {
    if (!subpartnerForm.subpartner_name.trim()) { toast.error('SubPartner name is required'); return; }
    const share = Number(subpartnerForm.subpartner_share);
    if (isNaN(share) || share < 0 || share > 100) { toast.error('Share must be between 0 and 100'); return; }
    try {
      setSavingSubpartner(true);
      const payload = { ...subpartnerForm, subpartner_share: share };
      if (isEdit && editingSubpartnerCode) {
        await axios.put(`${API_URL}/api/partner/subpartners/${editingSubpartnerCode}`, payload, getAuthHeaders());
        toast.success('SubPartner updated');
      } else {
        await axios.post(`${API_URL}/api/partner/subpartners`, payload, getAuthHeaders());
        toast.success('SubPartner created');
      }
      setShowAddSubpartner(false);
      setShowEditSubpartner(false);
      fetchSubpartners();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save subpartner'); }
    finally { setSavingSubpartner(false); }
  };

  // My Bonds functions
  const fetchMyBonds = useCallback(async () => {
    if (user?.role !== 'partner') return;
    try {
      setLoadingMyBonds(true);
      const response = await axios.get(`${API_URL}/api/partner/my-bonds`, getAuthHeaders());
      setMyBonds(response.data || []);
    } catch (error) { toast.error('Failed to load my bonds'); }
    finally { setLoadingMyBonds(false); }
  }, [getAuthHeaders, user?.role]);

  const fetchFamilies = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/partner/families`, getAuthHeaders());
      setFamilies(response.data || []);
    } catch (error) { toast.error('Failed to load families'); }
  }, [getAuthHeaders]);

  const fetchMaturityReport = useCallback(async (tillDate) => {
    if (!tillDate) return;
    try {
      setLoadingMaturity(true);
      const response = await axios.get(`${API_URL}/api/partner/maturity-report?till_date=${tillDate}`, getAuthHeaders());
      setMaturityReport(response.data || []);
    } catch (error) { toast.error('Failed to load maturity report'); }
    finally { setLoadingMaturity(false); }
  }, [getAuthHeaders]);

  const fetchBondHoldingsReport = useCallback(async (bondIsin) => {
    try {
      setLoadingBondHoldings(true);
      const url = bondIsin ? `${API_URL}/api/bond-holdings-report?bond_isin=${encodeURIComponent(bondIsin)}` : `${API_URL}/api/bond-holdings-report`;
      const response = await axios.get(url, getAuthHeaders());
      if (response.data.type === 'bond_summary') {
        setBondHoldingsData(response.data.rows || []);
        setBondClientBreakdown([]);
      } else {
        setBondClientBreakdown(response.data.rows || []);
      }
    } catch (error) { toast.error('Failed to load bond holdings report'); }
    finally { setLoadingBondHoldings(false); }
  }, [getAuthHeaders]);

  const viewBhTransactions = async (row) => {
    try {
      const response = await axios.get(`${API_URL}/api/partner/client/${row.client_id}/transactions/${encodeURIComponent(row.bond_isin)}`, getAuthHeaders());
      setBhTxnData(response.data);
      setBhTxnClient(row);
      setShowBhTxn(true);
    } catch (error) { toast.error('Failed to load transactions'); }
  };

  const getMaturityTillDate = useCallback((filter, customDate) => {
    const now = new Date();
    if (filter === '3m') { now.setMonth(now.getMonth() + 3); }
    else if (filter === '6m') { now.setMonth(now.getMonth() + 6); }
    else if (filter === '1y') { now.setFullYear(now.getFullYear() + 1); }
    else if (filter === 'custom' && customDate) { return customDate; }
    else { return null; }
    return now.toISOString().split('T')[0];
  }, []);

  const openMyBondModal = (bond = null) => {
    if (bond) {
      setEditingMyBond(bond.bond_isin);
      setMyBondForm({
        bond_isin: bond.bond_isin, bond_name: bond.bond_name || '',
        rating: bond.rating || '', yield_rate: bond.yield_rate ? String(bond.yield_rate * 100) : '',
        maturity_date: bond.maturity_date || '', maturity_pct_1: bond.maturity_pct_1 || '100', maturity_date_2: bond.maturity_date_2 || '', maturity_pct_2: bond.maturity_pct_2 || '', maturity_date_3: bond.maturity_date_3 || '', maturity_pct_3: bond.maturity_pct_3 || '',
        interest_payout_frequency: bond.interest_payout_frequency || '',
        available: bond.available !== false, bond_image_url: bond.bond_image_url || ''
      });
    } else {
      setEditingMyBond(null);
      setMyBondForm({ bond_isin: '', bond_name: '', rating: '', yield_rate: '', maturity_date: '', maturity_pct_1: '100', maturity_date_2: '', maturity_pct_2: '', maturity_date_3: '', maturity_pct_3: '', interest_payout_frequency: '', available: true, bond_image_url: '' });
    }
    setShowMyBondModal(true);
  };

  const handleSaveMyBond = async () => {
    if (!myBondForm.bond_isin.trim()) { toast.error('Bond ISIN is required'); return; }
    if (!myBondForm.bond_name.trim()) { toast.error('Bond Name is required'); return; }
    const bPct1 = Number(myBondForm.maturity_pct_1) || 100;
    if (bPct1 < 100) {
      const bTotal = bPct1 + (Number(myBondForm.maturity_pct_2) || 0) + (Number(myBondForm.maturity_pct_3) || 0);
      if (bTotal !== 100) { toast.error(`Maturity percentages must total 100% (currently ${bTotal}%)`); return; }
    }
    try {
      setSavingMyBond(true);
      const yieldVal = myBondForm.yield_rate ? parseFloat(myBondForm.yield_rate) / 100 : null;
      const payload = { ...myBondForm, yield_rate: yieldVal, maturity_pct_1: myBondForm.maturity_pct_1 ? Number(myBondForm.maturity_pct_1) : null, maturity_date_2: myBondForm.maturity_date_2 || null, maturity_pct_2: myBondForm.maturity_pct_2 ? Number(myBondForm.maturity_pct_2) : null, maturity_date_3: myBondForm.maturity_date_3 || null, maturity_pct_3: myBondForm.maturity_pct_3 ? Number(myBondForm.maturity_pct_3) : null };
      if (editingMyBond) {
        await axios.put(`${API_URL}/api/partner/my-bonds/${editingMyBond}`, payload, getAuthHeaders());
        toast.success('Bond updated');
      } else {
        await axios.post(`${API_URL}/api/partner/my-bonds`, payload, getAuthHeaders());
        toast.success('Bond added');
      }
      setShowMyBondModal(false);
      fetchMyBonds();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save bond'); }
    finally { setSavingMyBond(false); }
  };

  const handleDeleteMyBond = (isin, label) => {
    requestDelete({
      title: 'Delete Curated Bond',
      description: 'Remove this bond from your curated list?',
      itemLabel: label || isin,
      onConfirmed: async () => {
        await axios.delete(`${API_URL}/api/partner/my-bonds/${isin}`, getAuthHeaders());
        toast.success('Bond deleted');
        fetchMyBonds();
      },
    });
  };

  const handleMyBondImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await axios.post(`${API_URL}/api/partner/my-bonds/upload-image`, formData, {
        headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' }
      });
      const url = `${API_URL}${response.data.image_url}`;
      setMyBondForm(prev => ({ ...prev, bond_image_url: url }));
      toast.success('Image uploaded');
    } catch (error) { toast.error('Failed to upload image'); }
  };


  const fetchInvestmentOptions = useCallback(async (clientId) => {
    try {
      const url = clientId ? `${API_URL}/api/partner/investment-options?client_id=${clientId}` : `${API_URL}/api/partner/investment-options`;
      const response = await axios.get(url, getAuthHeaders());
      setInvestmentOptions(response.data || []);
    } catch (error) { /* investment options are optional, silently ignore */ }
  }, [getAuthHeaders]);

  // Memoize filtered SubPartner clients to avoid re-computation on every render
  const filteredSpClients = useMemo(() => 
    subpartnerClients.filter(c => spClientFilter === 'all' || c.subpartner_code === spClientFilter),
    [subpartnerClients, spClientFilter]
  );

  const openInvModal = (inv = null) => {
    setEditingInv(inv);
    if (inv) {
      setInvForm({ transaction_date: inv.transaction_date?.split(' ')[0] || '', bond_isin: inv.bond_isin || '', bond_name: inv.bond_name || '', quantity: inv.quantity || '', face_value: inv.face_value || '', invested_value: inv.invested_value || '', yield_rate: inv.yield_rate ? (inv.yield_rate < 1 ? (inv.yield_rate * 100) : inv.yield_rate) : '', maturity_date: inv.maturity_date?.split(' ')[0] || '', maturity_pct_1: inv.maturity_pct_1 || '100', maturity_date_2: inv.maturity_date_2?.split(' ')[0] || '', maturity_pct_2: inv.maturity_pct_2 || '', maturity_date_3: inv.maturity_date_3?.split(' ')[0] || '', maturity_pct_3: inv.maturity_pct_3 || '', client_name: inv.client_name || '', partner_share: inv.partner_share ? (inv.partner_share < 1 ? (inv.partner_share * 100) : inv.partner_share) : '', commn_type: inv.commn_type || 'Trail' });
    } else {
      setInvForm({ transaction_date: '', bond_isin: '', bond_name: '', quantity: '', face_value: '', invested_value: '', yield_rate: '', maturity_date: '', maturity_pct_1: '100', maturity_date_2: '', maturity_pct_2: '', maturity_date_3: '', maturity_pct_3: '', client_name: '', partner_share: '', commn_type: 'Trail' });
    }
    setShowInvModal(true);
  };

  const handleSaveInv = async () => {
    const { transaction_date, bond_isin, bond_name, quantity, face_value, invested_value, yield_rate, maturity_date, client_name } = invForm;
    if (!transaction_date || !bond_isin || !bond_name || !quantity || !face_value || !invested_value || !yield_rate || !maturity_date || !client_name) {
      toast.error('All fields are required'); return;
    }
    // Validate maturity % total
    const pct1 = Number(invForm.maturity_pct_1) || 100;
    if (pct1 < 100) {
      const total = pct1 + (Number(invForm.maturity_pct_2) || 0) + (Number(invForm.maturity_pct_3) || 0);
      if (total !== 100) { toast.error(`Maturity percentages must total 100% (currently ${total}%)`); return; }
    }
    try {
      setSavingInv(true);
      const payload = { ...invForm, quantity: Number(quantity), face_value: Number(face_value), invested_value: Number(invested_value), yield_rate: Number(yield_rate) <= 1 ? Number(yield_rate) : Number(yield_rate) / 100, maturity_date: invForm.maturity_date, maturity_pct_1: invForm.maturity_pct_1 ? Number(invForm.maturity_pct_1) : null, maturity_date_2: invForm.maturity_date_2 || null, maturity_pct_2: invForm.maturity_pct_2 ? Number(invForm.maturity_pct_2) : null, maturity_date_3: invForm.maturity_date_3 || null, maturity_pct_3: invForm.maturity_pct_3 ? Number(invForm.maturity_pct_3) : null, partner_share: invForm.partner_share ? Number(invForm.partner_share) : null, commn_type: invForm.commn_type || 'Trail' };
      if (editingInv) {
        await axios.put(`${API_URL}/api/partner/investments/${encodeURIComponent(editingInv.transaction_id)}`, payload, getAuthHeaders());
      } else {
        await axios.post(`${API_URL}/api/partner/investments`, payload, getAuthHeaders());
      }
      toast.success(`Investment ${editingInv ? 'updated' : 'added'} successfully`);
      setShowInvModal(false);
      fetchPartnerInvestments();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save investment'); }
    finally { setSavingInv(false); }
  };

  const openRedModal = (red = null) => {
    setEditingRed(red);
    if (red) {
      setRedForm({ investment_txn_id: '', transaction_date: red.transaction_date?.split(' ')[0] || '', quantity: red.quantity || '', redemption_value: red.redemption_value || '' });
    } else {
      setRedForm({ investment_txn_id: '', transaction_date: '', quantity: '', redemption_value: '' });
      setRedClientId('');
      setInvestmentOptions([]);
    }
    setShowRedModal(true);
  };

  const handleSaveRed = async () => {
    if ((!editingRed && !redForm.investment_txn_id) || !redForm.transaction_date || !redForm.quantity || !redForm.redemption_value) {
      toast.error('All fields are required'); return;
    }
    try {
      setSavingRed(true);
      if (editingRed) {
        await axios.put(`${API_URL}/api/partner/redemptions/${encodeURIComponent(editingRed.transaction_id)}`, { transaction_date: redForm.transaction_date, quantity: Number(redForm.quantity), redemption_value: Number(redForm.redemption_value) }, getAuthHeaders());
      } else {
        await axios.post(`${API_URL}/api/partner/redemptions`, { investment_txn_id: redForm.investment_txn_id, transaction_date: redForm.transaction_date, quantity: Number(redForm.quantity), redemption_value: Number(redForm.redemption_value) }, getAuthHeaders());
      }
      toast.success(`Redemption ${editingRed ? 'updated' : 'added'} successfully`);
      setShowRedModal(false);
      fetchPartnerRedemptions();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save redemption'); }
    finally { setSavingRed(false); }
  };

  const handleDeleteRed = (transaction_id, label) => {
    requestDelete({
      title: 'Delete Redemption',
      description: 'Permanently delete this redemption record?',
      itemLabel: label || transaction_id,
      onConfirmed: async () => {
        await axios.delete(`${API_URL}/api/partner/redemptions/${encodeURIComponent(transaction_id)}`, getAuthHeaders());
        toast.success('Redemption deleted');
        fetchPartnerRedemptions();
      },
    });
  };

  const viewClientPortfolio = async (client) => {
    try {
      setLoading(true);
      const [holdingsRes, redeemedRes, summaryRes] = await Promise.all([
        axios.get(`${API_URL}/api/partner/client/${client.client_id}/portfolio`, getAuthHeaders()),
        axios.get(`${API_URL}/api/partner/client/${client.client_id}/redeemed`, getAuthHeaders()),
        axios.get(`${API_URL}/api/partner/client/${client.client_id}/summary`, getAuthHeaders())
      ]);
      setClientHoldings(holdingsRes.data);
      setClientRedeemed(redeemedRes.data);
      setClientSummary(summaryRes.data);
      setSelectedClient(client);
    } catch (error) { toast.error('Failed to load client portfolio'); }
    finally { setLoading(false); }
  };

  const viewTransactions = async (holding) => {
    try {
      const response = await axios.get(`${API_URL}/api/partner/client/${selectedClient.client_id}/transactions/${encodeURIComponent(holding.bond_isin)}`, getAuthHeaders());
      setTransactions(response.data);
      setSelectedHolding(holding);
      setShowTransactions(true);
    } catch (error) { toast.error('Failed to load transactions'); }
  };

  const backToClients = () => { setSelectedClient(null); setClientHoldings([]); setClientRedeemed([]); setClientSummary(null); };

  const fetchCommission = async () => {
    try {
      setLoadingCommission(true);
      const response = await axios.get(`${API_URL}/api/partner/commission-report?month=${commissionMonth}&year=${commissionYear}`, getAuthHeaders());
      setCommissionRows(response.data.rows || []);
      setCommissionTotal(response.data.total_commission || 0);
      setCommissionMeta({ month_year: response.data.month_year, partner_name: response.data.partner_name });
      if (response.data.rows?.length === 0) toast.info('No commission data found for this period.');
    } catch (error) { toast.error('Failed to load commission report'); }
    finally { setLoadingCommission(false); }
  };

  const fetchPayout = useCallback(async () => {
    try {
      setLoadingPayout(true);
      const response = await axios.get(`${API_URL}/api/partner/payout-report`, getAuthHeaders());
      setPayoutRows(response.data.rows || []);
    } catch (error) { toast.error('Failed to load payout data'); }
    finally { setLoadingPayout(false); }
  }, [getAuthHeaders]);

  const fetchSbCommission = async () => {
    try {
      setLoadingSbComm(true);
      const response = await axios.get(`${API_URL}/api/partner/subbroker-commission?month=${sbCommMonth}&year=${sbCommYear}`, getAuthHeaders());
      setSbCommRows(response.data.rows || []);
      setSbCommTotal(response.data.total_commission || 0);
      setSbCommMeta({ month_year: response.data.month_year, partner_name: response.data.partner_name });
      if ((response.data.rows || []).length === 0) toast.info('No SubBroker commission data found for this period.');
    } catch (error) { toast.error('Failed to load SubBroker commission report'); }
    finally { setLoadingSbComm(false); }
  };

  const fetchSbPayout = async () => {
    try {
      setLoadingSbPayout(true);
      const response = await axios.get(`${API_URL}/api/partner/subbroker-payout?month=${sbPayoutMonth}&year=${sbPayoutYear}`, getAuthHeaders());
      setSbPayoutRows(response.data.rows || []);
    } catch (error) { toast.error('Failed to load SubBroker payout report'); }
    finally { setLoadingSbPayout(false); }
  };

  const computeMyBondCommission = async () => {
    try {
      setLoadingMbComm(true);
      const response = await axios.post(`${API_URL}/api/partner/compute-my-bond-commission`, { month: mbCommMonth, year: mbCommYear }, getAuthHeaders());
      setMbCommRows(response.data.rows || []);
      setMbCommTotal(response.data.total_commission || 0);
      setMbCommMeta({ month_year: response.data.month_year, partner_name: response.data.partner_name });
      toast.success(response.data.message || 'Commission computed');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to compute My Bond commission'); }
    finally { setLoadingMbComm(false); }
  };

  const fetchMyBondCommission = async () => {
    try {
      setLoadingMbComm(true);
      const response = await axios.get(`${API_URL}/api/partner/my-bond-commission-report?month=${mbCommMonth}&year=${mbCommYear}`, getAuthHeaders());
      setMbCommRows(response.data.rows || []);
      setMbCommTotal(response.data.total_commission || 0);
      setMbCommMeta({ month_year: response.data.month_year, partner_name: response.data.partner_name });
      if ((response.data.rows || []).length === 0) toast.info('No My Bond commission data found for this period.');
    } catch (error) { toast.error('Failed to load My Bond commission report'); }
    finally { setLoadingMbComm(false); }
  };

  const generateMyBondPayout = async () => {
    try {
      setLoadingMbPayout(true);
      const response = await axios.post(`${API_URL}/api/partner/generate-my-bond-payout`, { month: mbCommMonth, year: mbCommYear }, getAuthHeaders());
      toast.success(response.data.message || 'Payout generated');
      fetchMyBondPayout();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to generate My Bond payout'); }
    finally { setLoadingMbPayout(false); }
  };

  const fetchMyBondPayout = useCallback(async () => {
    try {
      setLoadingMbPayout(true);
      const response = await axios.get(`${API_URL}/api/partner/my-bond-payout-report`, getAuthHeaders());
      setMbPayoutRows(response.data.rows || []);
    } catch (error) { toast.error('Failed to load My Bond payout'); }
    finally { setLoadingMbPayout(false); }
  }, [getAuthHeaders]);

  const handleAddClient = async () => {
    if (!newClient.client_name.trim()) { toast.error('Client name is required'); return; }
    if (newClient.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newClient.email)) { toast.error('Invalid email address'); return; }
    if (newClient.mobile && !/^\d{10}$/.test(newClient.mobile)) { toast.error('Mobile number must be 10 digits'); return; }
    if (!cmrFile) { toast.error('CMR (PDF) is required'); return; }
    try {
      setSavingClient(true);
      const res = await axios.post(`${API_URL}/api/partner/add-client`, {
        client_name: newClient.client_name.trim(),
        email: newClient.email || null,
        mobile: newClient.mobile || null,
        subpartner_code: newClient.subpartner_code || null
      }, getAuthHeaders());
      // Upload CMR
      const clientId = res.data?.client_id;
      if (clientId && cmrFile) {
        const fd = new FormData();
        fd.append('file', cmrFile);
        await axios.post(`${API_URL}/api/upload-cmr/${clientId}`, fd, { ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' } });
      }
      toast.success('Client added successfully');
      setShowAddClient(false);
      setNewClient({ client_name: '', email: '', mobile: '', subpartner_code: '' });
      setCmrFile(null);
      fetchClients();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to add client'); }
    finally { setSavingClient(false); }
  };

  const openEditClient = async (client) => {
    setEditClient({ client_id: client.client_id, client_name: client.client_name, email: client.email || '', mobile: client.mobile || '', hasInvestments: false, subpartner_code: client.subpartner_code || '', cmr_path: client.cmr_path || '', cmr_filename: client.cmr_filename || '' });
    setCmrFile(null);
    setShowEditClient(true);
    try {
      const res = await axios.get(`${API_URL}/api/partner/client/${client.client_id}/has-investments`, getAuthHeaders());
      setEditClient(prev => ({ ...prev, hasInvestments: res.data.has_investments }));
    } catch (error) { /* silently handled - name edit check is optional */ }
  };

  const handleEditClient = async () => {
    if (!editClient.client_name.trim()) { toast.error('Client name is required'); return; }
    if (editClient.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editClient.email)) { toast.error('Invalid email address'); return; }
    if (editClient.mobile && !/^\d{10}$/.test(editClient.mobile)) { toast.error('Mobile number must be 10 digits'); return; }
    try {
      setSavingClient(true);
      await axios.put(`${API_URL}/api/partner/edit-client/${editClient.client_id}`, {
        client_name: editClient.client_name.trim(),
        email: editClient.email || null,
        mobile: editClient.mobile || null,
        subpartner_code: editClient.subpartner_code || null
      }, getAuthHeaders());
      // Upload CMR if file selected (only if CMR is currently empty for partner/subpartner)
      if (cmrFile) {
        const fd = new FormData();
        fd.append('file', cmrFile);
        await axios.post(`${API_URL}/api/upload-cmr/${editClient.client_id}`, fd, { ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' } });
      }
      toast.success('Client updated successfully');
      setShowEditClient(false);
      setCmrFile(null);
      fetchClients();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update client'); }
    finally { setSavingClient(false); }
  };

  // User Login functions
  const fetchUserLogins = useCallback(async () => {
    try {
      setLoadingUsers(true);
      const response = await axios.get(`${API_URL}/api/partner/user-logins`, getAuthHeaders());
      setUserLogins(response.data || []);
    } catch (error) { toast.error('Failed to load user logins'); }
    finally { setLoadingUsers(false); }
  }, [getAuthHeaders]);

  const handleAddUser = async () => {
    if (!newUser.username.trim()) { toast.error('Username is required'); return; }
    if (!newUser.password || newUser.password.length < 4) { toast.error('Password must be at least 4 characters'); return; }
    if (!newUser.entity_id) { toast.error(newUser.role === 'subpartner' ? 'Please select a SubPartner' : 'Please select a client'); return; }
    try {
      setSavingUser(true);
      await axios.post(`${API_URL}/api/partner/user-logins`, newUser, getAuthHeaders());
      toast.success('User login created');
      setShowAddUser(false);
      setNewUser({ username: '', password: '', entity_id: '', role: 'client' });
      fetchUserLogins();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create user login'); }
    finally { setSavingUser(false); }
  };

  const handleEditUser = async () => {
    if (!editUser.username.trim()) { toast.error('Username is required'); return; }
    if (editUser.password && editUser.password.length < 4) { toast.error('Password must be at least 4 characters'); return; }
    try {
      setSavingUser(true);
      await axios.put(`${API_URL}/api/partner/user-logins/${editUser.id}`, {
        username: editUser.username.trim(),
        password: editUser.password || null
      }, getAuthHeaders());
      toast.success('User login updated');
      setShowEditUser(false);
      fetchUserLogins();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update user login'); }
    finally { setSavingUser(false); }
  };

  const fetchPartnerInvestments = useCallback(async () => {
    try {
      setLoadingInvestments(true);
      const response = await axios.get(`${API_URL}/api/partner/investments`, getAuthHeaders());
      setPartnerInvestments(response.data || []);
    } catch (error) { toast.error('Failed to load investments'); }
    finally { setLoadingInvestments(false); }
  }, [getAuthHeaders]);

  const fetchPartnerRedemptions = useCallback(async () => {
    try {
      setLoadingRedemptions(true);
      const response = await axios.get(`${API_URL}/api/partner/redemptions`, getAuthHeaders());
      setPartnerRedemptions(response.data || []);
    } catch (error) { toast.error('Failed to load redemptions'); }
    finally { setLoadingRedemptions(false); }
  }, [getAuthHeaders]);

  const fetchCuratedBonds = useCallback(async () => {
    try {
      setLoadingCurated(true);
      const response = await axios.get(`${API_URL}/api/curated-bonds`, getAuthHeaders());
      setCuratedBonds(response.data || []);
    } catch (error) { toast.error('Failed to load curated bonds'); }
    finally { setLoadingCurated(false); }
  }, [getAuthHeaders]);

  const handleEnquiry = () => {
    if (!enquiryAmount || isNaN(enquiryAmount) || Number(enquiryAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    setSendingEnquiry(true);
    const message = `Client is interesting in investing in ${enquiryBond.bond_name} around INR ${Number(enquiryAmount).toLocaleString('en-IN')} . Pls confirm exact amount.`;
    const phone = (user?.role === 'subpartner' && partnerMobileNumber) ? `91${partnerMobileNumber}` : '918310791241';
    const waUrl = `https://api.whatsapp.com/send/?phone=${phone}&text=${encodeURIComponent(message)}`;
    setTimeout(() => {
      setSendingEnquiry(false);
      setShowEnquiryDialog(false);
      setEnquiryAmount('');
      window.open(waUrl, '_blank');
    }, 1000);
  };

  const handlePlaceRedeemRequest = (holding) => {
    setRedeemBond(holding);
    setRedeemQty('');
    setSendingRedeem(false);
    setShowRedeemDialog(true);
  };

  const handleSubmitRedeem = () => {
    if (!redeemQty || isNaN(redeemQty) || Number(redeemQty) <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }
    setSendingRedeem(true);
    const message = `Client wishes to redeem ${redeemBond.bond_name} Qty ${Number(redeemQty)} . Pls guide on the next steps.`;
    const phone = (user?.role === 'subpartner' && partnerMobileNumber) ? `91${partnerMobileNumber}` : '918310791241';
    const waUrl = `https://api.whatsapp.com/send/?phone=${phone}&text=${encodeURIComponent(message)}`;
    setTimeout(() => {
      setSendingRedeem(false);
      setShowRedeemDialog(false);
      setRedeemQty('');
      window.open(waUrl, '_blank');
    }, 1000);
  };

  const hasEligibleTxnForRedeem = () => {
    if (!transactions || !transactions.investments) return false;
    const allTxnIds = [...transactions.investments.map(t => t.transaction_id), ...transactions.redemptions.map(t => t.transaction_id)];
    return allTxnIds.some(id => String(id).startsWith('20'));
  };


  const exportToPDF = async () => {
    try {
      toast.info('Generating PDF with transactions...');
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      
      doc.setFontSize(20); doc.setTextColor(27, 77, 62);
      doc.text('Bond Portfolio Report', pageWidth / 2, 20, { align: 'center' });
      doc.setFontSize(12); doc.setTextColor(100);
      doc.text(selectedClient.client_name, pageWidth / 2, 28, { align: 'center' });
      doc.text('Generated: ' + new Date().toLocaleDateString('en-IN'), pageWidth / 2, 35, { align: 'center' });
      
      doc.setFontSize(14); doc.setTextColor(27, 77, 62);
      doc.text('Portfolio Summary', 14, 50);
      doc.setFontSize(10); doc.setTextColor(60);
      doc.text('Total Invested Value: ' + formatCurrency(clientSummary?.total_invested_value || 0), 14, 58);
      doc.text('Total Holdings: ' + (clientSummary?.total_holdings || 0), 14, 65);
      
      let currentY = 80;
      
      for (const holding of clientHoldings) {
        if (currentY > 250) { doc.addPage(); currentY = 20; }
        
        doc.setFontSize(12); doc.setTextColor(27, 77, 62);
        doc.text(holding.bond_name, 14, currentY);
        doc.setFontSize(9); doc.setTextColor(100);
        doc.text('ISIN: ' + holding.bond_isin + ' | Net Qty: ' + holding.net_quantity + ' | Invested: ' + formatCurrency(holding.invested_value), 14, currentY + 6);
        currentY += 12;
        
        try {
          const txnResponse = await axios.get(API_URL + '/api/partner/client/' + selectedClient.client_id + '/transactions/' + encodeURIComponent(holding.bond_isin), getAuthHeaders());
          const txnData = txnResponse.data;
          
          if (txnData.investments && txnData.investments.length > 0) {
            autoTable(doc, {
              startY: currentY,
              head: [['Date', 'Transaction ID', 'Qty', 'Invested Value', 'Yield']],
              body: txnData.investments.map(t => [formatDate(t.transaction_date), t.transaction_id, String(t.quantity), formatCurrency(t.invested_value), formatPercent(t.yield_rate)]),
              theme: 'striped', headStyles: { fillColor: [27, 77, 62], fontSize: 8 }, styles: { fontSize: 7 }, margin: { left: 14 }
            });
            currentY = doc.lastAutoTable.finalY + 5;
          }
          
          if (txnData.redemptions && txnData.redemptions.length > 0) {
            autoTable(doc, {
              startY: currentY,
              head: [['Date', 'Transaction ID', 'Qty', 'Redemption Value', 'Yield']],
              body: txnData.redemptions.map(t => [formatDate(t.transaction_date), t.transaction_id, String(t.quantity), formatCurrency(t.redemption_value), formatPercent(t.yield_rate)]),
              theme: 'striped', headStyles: { fillColor: [212, 175, 55], fontSize: 8 }, styles: { fontSize: 7 }, margin: { left: 14 }
            });
            currentY = doc.lastAutoTable.finalY + 5;
          }
        } catch (err) { console.warn('Failed to fetch holding transactions:', err?.message); }
        currentY += 10;
      }
      
      doc.save('BondMaster_' + selectedClient.client_name + '_Portfolio_' + new Date().toISOString().split('T')[0] + '.pdf');
      toast.success('PDF exported successfully');
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };

  const exportToExcel = async () => {
    try {
      toast.info('Generating Excel with transactions...');
      const wb = XLSX.utils.book_new();
      
      const holdingsData = clientHoldings.map(h => ({ 'Bond ISIN': h.bond_isin, 'Bond Name': h.bond_name, 'Net Quantity': h.net_quantity, 'Invested Value': h.invested_value }));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(holdingsData), 'Holdings Summary');
      
      let allInvestments = [];
      let allRedemptions = [];
      
      for (const holding of clientHoldings) {
        try {
          const txnResponse = await axios.get(API_URL + '/api/partner/client/' + selectedClient.client_id + '/transactions/' + encodeURIComponent(holding.bond_isin), getAuthHeaders());
          const txnData = txnResponse.data;
          
          if (txnData.investments) {
            txnData.investments.forEach(t => {
              allInvestments.push({ 'Bond ISIN': holding.bond_isin, 'Bond Name': holding.bond_name, 'Transaction Date': formatDate(t.transaction_date), 'Transaction ID': t.transaction_id, 'Quantity': t.quantity, 'Invested Value': t.invested_value, 'Yield': t.yield_rate });
            });
          }
          if (txnData.redemptions) {
            txnData.redemptions.forEach(t => {
              allRedemptions.push({ 'Bond ISIN': holding.bond_isin, 'Bond Name': holding.bond_name, 'Transaction Date': formatDate(t.transaction_date), 'Transaction ID': t.transaction_id, 'Quantity': t.quantity, 'Redemption Value': t.redemption_value, 'Yield': t.yield_rate });
            });
          }
        } catch (err) { console.warn('Failed to fetch holding transactions:', err?.message); }
      }
      
      if (allInvestments.length > 0) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(allInvestments), 'Investment Transactions');
      if (allRedemptions.length > 0) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(allRedemptions), 'Redemption Transactions');
      if (clientRedeemed.length > 0) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(clientRedeemed.map(h => ({ 'Bond ISIN': h.bond_isin, 'Bond Name': h.bond_name }))), 'Fully Redeemed');
      
      saveAs(new Blob([XLSX.write(wb, { bookType: 'xlsx', type: 'array' })], { type: 'application/octet-stream' }), 'BondMaster_' + selectedClient.client_name + '_Portfolio_' + new Date().toISOString().split('T')[0] + '.xlsx');
      toast.success('Excel exported successfully');
    } catch (error) {
      toast.error('Failed to export Excel');
    }
  };

  // Pie chart data memoized
  const clientsPieData = useMemo(() => clients.filter(c => c.total_invested_value > 0).map((c) => ({
    name: c.client_name.length > 20 ? c.client_name.substring(0, 20) + '...' : c.client_name,
    value: c.total_invested_value,
    fullName: c.client_name
  })), [clients]);

  // Pie chart for client-wise invested value
  const ClientsPieChart = () => {
    const chartData = clientsPieData;

    const CustomTooltip = ({ active, payload }) => {
      if (active && payload && payload.length) {
        return (
          <div className="bg-white p-3 rounded-lg shadow-lg border">
            <p className="font-medium text-[#1B4D3E] text-sm">{payload[0].payload.fullName}</p>
            <p className="text-[#B8860B] font-semibold">{formatCurrency(payload[0].value)}</p>
          </div>
        );
      }
      return null;
    };

    const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
      if (percent < 0.05) return null;
      const RADIAN = Math.PI / 180;
      const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
      const x = cx + radius * Math.cos(-midAngle * RADIAN);
      const y = cy + radius * Math.sin(-midAngle * RADIAN);
      return (
        <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={600}>
          {`${(percent * 100).toFixed(0)}%`}
        </text>
      );
    };

    if (chartData.length === 0) return null;

    return (
      <div className="h-[200px]">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={chartData} cx="50%" cy="50%" labelLine={false} label={renderCustomLabel} outerRadius={80} fill="#8884d8" dataKey="value">
              {chartData.map((entry, index) => (<Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const partnerNavItems = useMemo(() => {
    const items = [
      { key: 'clients', label: 'Clients', icon: Building2, testId: 'partner-clients-tab' },
      { key: 'families', label: 'Families', icon: Home, testId: 'partner-families-tab', onSelect: () => { if (families.length === 0) fetchFamilies(); } },
      { key: 'investments', label: 'Investments', icon: TrendingUp, testId: 'partner-investments-tab', onSelect: () => { if (partnerInvestments.length === 0) fetchPartnerInvestments(); } },
      { key: 'redemptions', label: 'Redemptions', icon: ArrowUpFromLine, testId: 'partner-redemptions-tab', onSelect: () => { if (partnerRedemptions.length === 0) fetchPartnerRedemptions(); } },
      { key: 'curated-bonds', label: 'Curated Bonds', icon: CheckCircle, testId: 'partner-curated-bonds-tab', onSelect: () => { if (curatedBonds.length === 0) fetchCuratedBonds(); } },
      { key: 'maturity-report', label: 'Maturity Report', icon: CalendarDays, testId: 'partner-maturity-report-tab', onSelect: () => { const td = getMaturityTillDate(maturityFilter, maturityCustomDate); if (td) fetchMaturityReport(td); } },
      { key: 'bond-holdings', label: 'Bond Holdings', icon: Briefcase, testId: 'partner-bond-holdings-tab', onSelect: () => { if (bondHoldingsData.length === 0) fetchBondHoldingsReport(); } },
    ];
    if (!(user?.role === 'subpartner' && partnerConfig.hide_commission_from_subpartners)) {
      items.push({ divider: true });
      items.push({ key: 'commission', label: 'Commission', icon: Calculator, testId: 'partner-commission-tab' });
      items.push({ key: 'payout', label: 'Payout', icon: CreditCard, testId: 'partner-payout-tab', onSelect: () => { if (payoutRows.length === 0) fetchPayout(); } });
    }
    items.push({ divider: true });
    items.push({ key: 'user-logins', label: 'User Logins', icon: UserPlus, testId: 'partner-user-logins-tab', onSelect: () => { if (userLogins.length === 0) fetchUserLogins(); } });
    if (partnerConfig.allow_subpartner && user?.role === 'partner') {
      items.push({ divider: true });
      items.push({ label: 'SubBroker', sectionLabel: true });
      items.push({ key: 'subpartners', label: 'SubPartners', icon: Users, testId: 'partner-subpartners-tab', onSelect: () => { if (subpartners.length === 0) fetchSubpartners(); } });
      items.push({ key: 'sp-clients', label: 'SP Clients', icon: Building2, testId: 'partner-sp-clients-tab', onSelect: () => { fetchSubpartnerClients(); if (subpartners.length === 0) fetchSubpartners(); } });
      items.push({ key: 'sb-commission', label: 'SB Commission', icon: Calculator, testId: 'partner-sb-commission-tab' });
      items.push({ key: 'sb-payout', label: 'SB Payout', icon: CreditCard, testId: 'partner-sb-payout-tab' });
    }
    if (partnerConfig.allow_my_bond_commission && user?.role === 'partner') {
      items.push({ divider: true });
      items.push({ label: 'My Bond', sectionLabel: true });
      items.push({ key: 'mb-commission', label: 'MB Commission', icon: Calculator, testId: 'partner-mb-commission-tab' });
      items.push({ key: 'mb-payout', label: 'MB Payout', icon: CreditCard, testId: 'partner-mb-payout-tab', onSelect: () => { if (mbPayoutRows.length === 0) fetchMyBondPayout(); } });
    }
    if (partnerConfig.allow_my_bonds && user?.role === 'partner') {
      items.push({ key: 'my-bonds', label: 'My Bonds', icon: FileText, testId: 'partner-my-bonds-tab', onSelect: () => { if (myBonds.length === 0) fetchMyBonds(); } });
    }
    return items;
  }, [user?.role, partnerConfig, families.length, partnerInvestments.length, partnerRedemptions.length, curatedBonds.length, payoutRows.length, userLogins.length, subpartners.length, mbPayoutRows.length, myBonds.length, maturityFilter, maturityCustomDate, bondHoldingsData.length, fetchFamilies, fetchPartnerInvestments, fetchPartnerRedemptions, fetchCuratedBonds, fetchPayout, fetchUserLogins, fetchSubpartners, fetchSubpartnerClients, fetchMyBondPayout, fetchMyBonds, fetchMaturityReport, getMaturityTillDate, fetchBondHoldingsReport]);

  const handlePartnerNav = useCallback((item) => {
    setActiveTab(item.key);
    if (item.onSelect) item.onSelect();
    setSidebarOpen(false);
  }, []);

  const signupUrl = useMemo(() => {
    if (!partnerConfig.partner_code) return '';
    return user?.role === 'subpartner'
      ? `${window.location.origin}/signup?pc=${partnerConfig.partner_code}&subpc=${user.entity_id}`
      : `${window.location.origin}/signup?pc=${partnerConfig.partner_code}`;
  }, [partnerConfig.partner_code, user?.role, user?.entity_id]);

  if (loading && !selectedClient) return <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: colors.card_bg }}><div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: colors.primary }}></div></div>;

  return (
    <div className="dashboard-container" data-testid="partner-dashboard">
      {/* Mobile header */}
      <div className="mobile-header" style={{ borderColor: `${colors.primary}20` }}>
        <button className="hamburger-btn" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></button>
        <img src={logoUrl} alt="Logo" className="mobile-header-logo" />
        <div style={{ width: '2.25rem' }} />
      </div>

      {/* Sidebar overlay (mobile) */}
      <div className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`} onClick={() => setSidebarOpen(false)} />

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`} style={{ backgroundColor: colors.sidebar_bg }}>
        <div className="sidebar-logo">
          <img src={logoUrl} alt="Logo" className="h-20 max-w-[200px] object-contain" />
        </div>
        <div className="sidebar-user">
          <div className="flex items-center gap-3 p-2.5 rounded-lg" style={{ backgroundColor: colors.light_bg }}>
            <Users className="h-5 w-5" style={{ color: colors.accent }} />
            <div>
              <p className="text-xs" style={{ color: colors.text_secondary }}>{user?.role === 'subpartner' ? 'SubPartner' : 'Partner'}</p>
              <p className="font-medium text-sm truncate" style={{ color: colors.accent }} data-testid="partner-name">{user.entity_name || user.username}</p>
            </div>
          </div>
        </div>
        <nav className="sidebar-nav" data-testid="partner-sidebar-nav">
          {partnerNavItems.map((item, idx) =>
            item.divider ? <div key={`d-${idx}`} className="sidebar-nav-divider" /> :
            item.sectionLabel ? <div key={`l-${idx}`} className="sidebar-nav-label">{item.label}</div> : (
              <button
                key={item.key}
                className={`sidebar-nav-item ${activeTab === item.key ? 'active' : ''}`}
                style={{ '--nav-active-bg': colors.primary }}
                onClick={() => handlePartnerNav(item)}
                data-testid={item.testId}
              >
                <item.icon className="nav-icon" />{item.label}
              </button>
            )
          )}
        </nav>
        {/* Signup link in sidebar */}
        {signupUrl && (
          <div className="px-2 py-2 border-t" style={{ borderColor: '#e2e8f0' }}>
            <p className="text-[0.625rem] font-semibold uppercase tracking-wider mb-1.5" style={{ color: colors.text_secondary }}>Signup Link</p>
            <div className="flex items-center gap-1.5">
              <code className="text-[0.6rem] px-1.5 py-1 rounded flex-1 truncate" style={{ backgroundColor: colors.light_bg, color: colors.text_secondary }} data-testid="signup-link-url">{signupUrl}</code>
              <button
                className="flex-shrink-0 p-1.5 rounded-md border transition-colors"
                style={{ borderColor: `${colors.accent}40`, color: linkCopied ? '#16a34a' : colors.accent }}
                data-testid="copy-signup-link-btn"
                onClick={() => {
                  if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(signupUrl).catch(() => {});
                  setLinkCopied(true);
                  toast.success('Signup link copied!');
                  setTimeout(() => setLinkCopied(false), 2000);
                }}
              >
                {linkCopied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              </button>
            </div>
          </div>
        )}
        <div className="sidebar-footer">
          <Button variant="outline" className="w-full justify-start text-sm h-9" style={{ color: colors.text_secondary }} onClick={() => logout()} data-testid="logout-btn"><LogOut className="h-4 w-4 mr-2" />Sign Out</Button>
        </div>
      </aside>
      <main className="main-content">
        {!selectedClient ? (
          <>
            <div className="mb-6">
              <h1 className="text-2xl md:text-3xl font-heading font-bold mb-1" style={{ color: colors.text_primary }}>{user?.role === 'subpartner' ? 'SubPartner Dashboard' : 'Partner Dashboard'}</h1>
              <p style={{ color: colors.text_secondary }}>View and manage your clients' portfolios</p>
            </div>

            {/* Summary Cards and Pie Chart in same row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              <Card className="summary-card border-0" data-testid="summary-total-clients">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-lg" style={{ backgroundColor: colors.light_bg }}>
                      <Users className="h-6 w-6" style={{ color: colors.primary }} />
                    </div>
                    <div>
                      <p className="text-sm" style={{ color: colors.text_secondary }}>Total Clients</p>
                      <p className="text-2xl font-bold" style={{ color: colors.primary }}>{summary.total_clients}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="summary-card border-0" data-testid="summary-total-invested">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-lg" style={{ backgroundColor: colors.light_bg }}>
                      <Wallet className="h-6 w-6" style={{ color: colors.accent }} />
                    </div>
                    <div>
                      <p className="text-sm" style={{ color: colors.text_secondary }}>Total Invested Value</p>
                      <p className="text-xl font-bold" style={{ color: colors.accent }}>{formatCurrency(summary.total_invested_value)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-sm">
                <CardHeader className="pb-0 pt-4">
                  <CardTitle className="text-sm font-medium" style={{ color: colors.primary }}>Client-wise Allocation</CardTitle>
                </CardHeader>
                <CardContent className="pb-4">
                  <ClientsPieChart />
                </CardContent>
              </Card>
            </div>

            <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); }} className="w-full">
              <TabsList className="hidden">
                {partnerNavItems.filter(i => !i.divider && !i.sectionLabel).map(i => <TabsTrigger key={i.key} value={i.key}>{i.label}</TabsTrigger>)}
              </TabsList>
              <TabsContent value="clients">
                <Card className="holdings-card border-0" data-testid="clients-list">
                  <CardHeader className="flex flex-row items-center justify-between border-b" style={{ backgroundColor: colors.card_bg }}>
                    <CardTitle className="text-lg font-heading flex items-center gap-2" style={{ color: colors.primary }}><Building2 className="h-5 w-5" />Your Clients</CardTitle>
                    <Button size="sm" style={{ backgroundColor: colors.primary }} className="text-white" onClick={() => setShowAddClient(true)} data-testid="partner-add-client-btn"><Plus className="h-4 w-4 mr-1" />Add Client</Button>
                  </CardHeader>
                  <CardContent className="p-0">
                    <Table className="holdings-table">
                      <TableHeader>
                        <TableRow style={{ backgroundColor: `${colors.light_bg}80` }}>
                          <TableHead className="font-semibold" style={{ color: colors.primary }}>Client Name</TableHead>
                          <TableHead className="font-semibold text-right" style={{ color: colors.primary }}>Total Invested Value</TableHead>
                          <TableHead className="font-semibold text-center" style={{ color: colors.primary }}>Action</TableHead>
                          <TableHead className="font-semibold" style={{ color: colors.primary }}>Email</TableHead>
                          <TableHead className="font-semibold" style={{ color: colors.primary }}>Mobile</TableHead>
                          <TableHead className="font-semibold text-center" style={{ color: colors.primary }}>CMR</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {clients.map((client, idx) => (
                          <TableRow key={client.client_id} className="animate-fadeIn cursor-pointer" style={{ animationDelay: `${idx * 0.05}s` }}>
                            <TableCell className="font-medium">{client.client_name}</TableCell>
                            <TableCell className="text-right font-semibold" style={{ color: colors.accent }}>{formatCurrency(client.total_invested_value)}</TableCell>
                            <TableCell className="text-center">
                              <div className="flex items-center justify-center gap-1">
                                <Button variant="ghost" size="sm" onClick={() => openEditClient(client)} data-testid={`edit-client-${client.client_id}`}><Pencil className="h-4 w-4" /></Button>
                                <Button size="sm" style={{ backgroundColor: colors.primary }} className="text-white" onClick={() => viewClientPortfolio(client)} data-testid={`view-client-${client.client_id}`}><Search className="h-4 w-4 mr-2" />View Portfolio</Button>
                              </div>
                            </TableCell>
                            <TableCell>{client.email || '-'}</TableCell>
                            <TableCell>{client.mobile || '-'}</TableCell>
                            <TableCell className="text-center">{client.cmr_path ? <Button variant="ghost" size="sm" className="text-red-600" onClick={async () => { try { const res = await axios.get(`${API_URL}/api/download-cmr/${client.client_id}`, { ...getAuthHeaders(), responseType: 'blob' }); const url = window.URL.createObjectURL(res.data); const a = document.createElement('a'); a.href = url; a.download = client.cmr_filename || 'cmr.pdf'; a.click(); window.URL.revokeObjectURL(url); } catch { toast.error('Failed to download CMR'); } }} data-testid={`download-cmr-${client.client_id}`}><FileDown className="h-4 w-4" /></Button> : <span className="text-gray-400 text-xs">-</span>}</TableCell>
                          </TableRow>
                        ))}
                        {clients.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8" style={{ color: colors.text_secondary }}>No clients assigned to you</TableCell></TableRow>}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>
              <TabsContent value="investments">
                <Card className="border-0 shadow-sm" data-testid="partner-investments-list">
                  <CardHeader className="flex flex-row items-center justify-between border-b" style={{ backgroundColor: colors.card_bg }}>
                    <CardTitle className="text-lg font-heading flex items-center gap-2" style={{ color: colors.primary }}><TrendingUp className="h-5 w-5" />Investments</CardTitle>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" style={{ borderColor: colors.primary, color: colors.primary }} onClick={fetchPartnerInvestments} disabled={loadingInvestments} data-testid="partner-refresh-investments">
                        {loadingInvestments ? <span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full inline-block" /> : <Search className="h-4 w-4 mr-1" />}
                        Refresh
                      </Button>
                      {partnerConfig.allow_add_investment_redemption && user?.role !== 'subpartner' && (
                        <Button size="sm" style={{ backgroundColor: colors.primary }} className="text-white" onClick={() => openInvModal()} data-testid="partner-add-investment-btn"><Plus className="h-4 w-4 mr-1" />Add Investment</Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="p-0 overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow style={{ backgroundColor: `${colors.light_bg}50` }}>
                          <TableHead className="font-semibold" style={{ color: colors.primary }}>Transaction Date</TableHead>
                          <TableHead className="font-semibold" style={{ color: colors.primary }}>Bond ISIN</TableHead>
                          <TableHead className="font-semibold" style={{ color: colors.primary }}>Bond Name</TableHead>
                          <TableHead className="font-semibold" style={{ color: colors.primary }}>Client</TableHead>
                          <TableHead className="font-semibold text-right" style={{ color: colors.primary }}>Qty</TableHead>
                          <TableHead className="font-semibold text-right" style={{ color: colors.primary }}>Invested Value</TableHead>
                          {partnerConfig.allow_add_investment_redemption && user?.role !== 'subpartner' && <TableHead className="font-semibold text-center" style={{ color: colors.primary }}>Action</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {partnerInvestments.map((inv, idx) => (
                          <TableRow key={inv.transaction_id} className="animate-fadeIn" style={{ animationDelay: `${idx * 0.03}s` }}>
                            <TableCell>{formatDate(inv.transaction_date)}</TableCell>
                            <TableCell className="font-mono text-sm">{inv.bond_isin}</TableCell>
                            <TableCell className="max-w-[200px] truncate" title={inv.bond_name}>{inv.bond_name}</TableCell>
                            <TableCell>{inv.client_name}</TableCell>
                            <TableCell className="text-right font-semibold" style={{ color: colors.primary }}>{inv.quantity}</TableCell>
                            <TableCell className="text-right font-semibold" style={{ color: colors.accent }}>{formatCurrency(inv.invested_value)}</TableCell>
                            {partnerConfig.allow_add_investment_redemption && user?.role !== 'subpartner' && (
                              <TableCell className="text-center">
                                {String(inv.transaction_id).startsWith('TXN') ? (
                                  <Button variant="ghost" size="sm" onClick={() => openInvModal(inv)} data-testid={`edit-inv-${inv.transaction_id}`}><Pencil className="h-4 w-4" /></Button>
                                ) : <span className="text-gray-400 text-xs">—</span>}
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                        {partnerInvestments.length === 0 && (
                          <TableRow><TableCell colSpan={(partnerConfig.allow_add_investment_redemption && user?.role !== 'subpartner') ? 7 : 6} className="text-center py-8" style={{ color: colors.text_secondary }}>{loadingInvestments ? 'Loading...' : 'No investment records found'}</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>
              <TabsContent value="redemptions">
                <Card className="border-0 shadow-sm" data-testid="partner-redemptions-list">
                  <CardHeader className="flex flex-row items-center justify-between border-b" style={{ backgroundColor: colors.card_bg }}>
                    <CardTitle className="text-lg font-heading flex items-center gap-2" style={{ color: colors.accent }}><ArrowUpFromLine className="h-5 w-5" />Redemptions</CardTitle>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" style={{ borderColor: colors.accent, color: colors.accent }} onClick={fetchPartnerRedemptions} disabled={loadingRedemptions} data-testid="partner-refresh-redemptions">
                        {loadingRedemptions ? <span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full inline-block" /> : <Search className="h-4 w-4 mr-1" />}
                        Refresh
                      </Button>
                      {partnerConfig.allow_add_investment_redemption && user?.role !== 'subpartner' && (
                        <Button size="sm" style={{ backgroundColor: colors.accent }} className="text-white" onClick={() => openRedModal()} data-testid="partner-add-redemption-btn"><Plus className="h-4 w-4 mr-1" />Add Redemption</Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="p-0 overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow style={{ backgroundColor: `${colors.accent}15` }}>
                          <TableHead className="font-semibold" style={{ color: colors.accent }}>Transaction Date</TableHead>
                          <TableHead className="font-semibold" style={{ color: colors.accent }}>Bond ISIN</TableHead>
                          <TableHead className="font-semibold" style={{ color: colors.accent }}>Bond Name</TableHead>
                          <TableHead className="font-semibold" style={{ color: colors.accent }}>Client</TableHead>
                          <TableHead className="font-semibold text-right" style={{ color: colors.accent }}>Qty</TableHead>
                          <TableHead className="font-semibold text-right" style={{ color: colors.accent }}>Redemption Value</TableHead>
                          {partnerConfig.allow_add_investment_redemption && user?.role !== 'subpartner' && <TableHead className="font-semibold text-center" style={{ color: colors.accent }}>Action</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {partnerRedemptions.map((red, idx) => (
                          <TableRow key={red.transaction_id} className="animate-fadeIn" style={{ animationDelay: `${idx * 0.03}s` }}>
                            <TableCell>{formatDate(red.transaction_date)}</TableCell>
                            <TableCell className="font-mono text-sm">{red.bond_isin}</TableCell>
                            <TableCell className="max-w-[200px] truncate" title={red.bond_name}>{red.bond_name}</TableCell>
                            <TableCell>{red.client_name}</TableCell>
                            <TableCell className="text-right font-semibold" style={{ color: colors.accent }}>{red.quantity}</TableCell>
                            <TableCell className="text-right font-semibold" style={{ color: colors.primary }}>{formatCurrency(red.redemption_value)}</TableCell>
                            {partnerConfig.allow_add_investment_redemption && user?.role !== 'subpartner' && (
                              <TableCell className="text-center">
                                {String(red.transaction_id).startsWith('TXN') ? (
                                  <><Button variant="ghost" size="sm" onClick={() => openRedModal(red)} data-testid={`edit-red-${red.transaction_id}`}><Pencil className="h-4 w-4" /></Button><Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteRed(red.transaction_id)} data-testid={`delete-red-${red.transaction_id}`}><Trash2 className="h-4 w-4" /></Button></>
                                ) : <span className="text-gray-400 text-xs">—</span>}
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                        {partnerRedemptions.length === 0 && (
                          <TableRow><TableCell colSpan={(partnerConfig.allow_add_investment_redemption && user?.role !== 'subpartner') ? 7 : 6} className="text-center py-8" style={{ color: colors.text_secondary }}>{loadingRedemptions ? 'Loading...' : 'No redemption records found'}</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>
              <TabsContent value="curated-bonds">
                <Card className="border-0 shadow-sm" data-testid="partner-curated-bonds-list">
                  <CardHeader className="flex flex-row items-center justify-between border-b" style={{ backgroundColor: colors.card_bg }}>
                    <CardTitle className="text-lg font-heading flex items-center gap-2" style={{ color: colors.primary }}><CheckCircle className="h-5 w-5" style={{ color: colors.accent }} />Curated Bonds</CardTitle>
                    <Button variant="outline" size="sm" style={{ borderColor: colors.primary, color: colors.primary }} onClick={fetchCuratedBonds} disabled={loadingCurated} data-testid="partner-refresh-curated-bonds">
                      {loadingCurated ? <span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full inline-block" /> : <Search className="h-4 w-4 mr-1" />}Refresh
                    </Button>
                  </CardHeader>
                  <CardContent className="p-0 overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow style={{ backgroundColor: `${colors.light_bg}50` }}>
                          <TableHead className="font-semibold" style={{ color: colors.primary }}>Bond ISIN</TableHead>
                          <TableHead className="font-semibold" style={{ color: colors.primary }}>Name</TableHead>
                          <TableHead className="font-semibold" style={{ color: colors.primary }}>Rating</TableHead>
                          <TableHead className="font-semibold text-right" style={{ color: colors.primary }}>Yield</TableHead>
                          <TableHead className="font-semibold" style={{ color: colors.primary }}>Maturity</TableHead>
                          <TableHead className="font-semibold" style={{ color: colors.primary }}>Int. Payout Freq.</TableHead>
                          <TableHead className="font-semibold text-center" style={{ color: colors.primary }}>Enquire</TableHead>
                          <TableHead className="font-semibold text-center" style={{ color: colors.primary }}>Image</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {curatedBonds.map((bond, idx) => (
                          <TableRow key={bond.bond_isin} className="animate-fadeIn" style={{ animationDelay: `${idx * 0.03}s` }}>
                            <TableCell className="font-mono text-sm">{bond.bond_isin}</TableCell>
                            <TableCell className="font-medium max-w-[200px] truncate" title={bond.bond_name}>{bond.bond_name}</TableCell>
                            <TableCell><span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: colors.light_bg, color: colors.primary }}>{bond.rating || '-'}</span></TableCell>
                            <TableCell className="text-right font-semibold" style={{ color: colors.accent }}>{bond.yield_rate ? `${bond.yield_rate}%` : '-'}</TableCell>
                            <TableCell>{(bond.maturity_pct_2 || bond.maturity_pct_3) ? <MaturityBadge maturityDate={bond.maturity_date} maturityPct1={bond.maturity_pct_1} maturityDate2={bond.maturity_date_2} maturityPct2={bond.maturity_pct_2} maturityDate3={bond.maturity_date_3} maturityPct3={bond.maturity_pct_3} /> : (bond.maturity_date ? formatDate(bond.maturity_date) : '-')}</TableCell>
                            <TableCell>{bond.interest_payout_frequency || '-'}</TableCell>
                            <TableCell className="text-center">
                              <Button variant="ghost" size="sm" onClick={() => { setEnquiryBond(bond); setEnquiryAmount(''); setSendingEnquiry(false); setShowEnquiryDialog(true); }} data-testid={`partner-enquire-${bond.bond_isin}`}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                              </Button>
                            </TableCell>
                            <TableCell className="text-center">
                              {bond.bond_image_url ? (
                                <Button variant="ghost" size="sm" onClick={() => { setPreviewBondImage(bond.bond_image_url); setShowBondImagePreview(true); }} data-testid={`partner-curated-view-image-${bond.bond_isin}`}>
                                  <ImageIcon className="h-4 w-4" style={{ color: colors.primary }} />
                                </Button>
                              ) : <span className="text-gray-400 text-xs">—</span>}
                            </TableCell>
                          </TableRow>
                        ))}
                        {curatedBonds.length === 0 && (
                          <TableRow><TableCell colSpan={8} className="text-center py-8" style={{ color: colors.text_secondary }}>{loadingCurated ? 'Loading...' : 'No curated bonds available'}</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>
              <TabsContent value="commission">
                <Card className="border-0 shadow-sm mb-6">
                  <CardHeader className="border-b" style={{ backgroundColor: colors.card_bg }}>
                    <CardTitle className="text-lg font-heading flex items-center gap-2" style={{ color: colors.primary }}><Calculator className="h-5 w-5" />Commission Report</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="flex flex-wrap items-end gap-4">
                      <div className="w-44">
                        <label className="text-sm font-medium mb-1 block" style={{ color: colors.text_secondary }}>Month</label>
                        <Select value={String(commissionMonth)} onValueChange={(v) => setCommissionMonth(Number(v))}>
                          <SelectTrigger data-testid="partner-commission-month"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {monthOptions.map(m => <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="w-28">
                        <label className="text-sm font-medium mb-1 block" style={{ color: colors.text_secondary }}>Year</label>
                        <input type="number" value={commissionYear} onChange={(e) => setCommissionYear(Number(e.target.value))} min={2000} max={2099} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="partner-commission-year" />
                      </div>
                      <Button style={{ backgroundColor: colors.primary }} className="text-white" onClick={fetchCommission} disabled={loadingCommission} data-testid="partner-fetch-commission-btn">
                        {loadingCommission ? <span className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full inline-block" /> : <Search className="h-4 w-4 mr-2" />}
                        View Report
                      </Button>
                    </div>
                  </CardContent>
                </Card>
                {commissionMeta && (
                  <Card className="border-0 shadow-sm">
                    <CardHeader className="flex flex-row items-center justify-between border-b" style={{ backgroundColor: `${colors.light_bg}80` }}>
                      <div>
                        <CardTitle className="text-lg font-heading" style={{ color: colors.primary }}>Commission: {commissionMeta.month_year}</CardTitle>
                      </div>
                      <div className="text-right">
                        <p className="text-sm" style={{ color: colors.text_secondary }}>Total Commission</p>
                        <p className="text-2xl font-bold" style={{ color: colors.primary }}>{formatCurrency(commissionTotal)}</p>
                      </div>
                    </CardHeader>
                    <CardContent className="p-0 overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow style={{ backgroundColor: `${colors.light_bg}50` }}>
                            <TableHead className="font-semibold" style={{ color: colors.primary }}>Client Name</TableHead>
                            <TableHead className="font-semibold" style={{ color: colors.primary }}>Txn Date</TableHead>
                            <TableHead className="font-semibold" style={{ color: colors.primary }}>Bond Name</TableHead>
                            <TableHead className="font-semibold" style={{ color: colors.primary }}>Type</TableHead>
                            <TableHead className="font-semibold text-right" style={{ color: colors.primary }}>Net Qty</TableHead>
                            <TableHead className="font-semibold text-right" style={{ color: colors.primary }}>Net Face Value</TableHead>
                            <TableHead className="font-semibold text-center" style={{ color: colors.primary }}>Days Held</TableHead>
                            <TableHead className="font-semibold text-right" style={{ color: colors.primary }}>Commission</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {commissionRows.map((row, idx) => (
                            <TableRow key={`${row.client_id}-${row.bond_isin}-${row.type}`} className="animate-fadeIn" style={{ animationDelay: `${idx * 0.03}s` }}>
                              <TableCell className="font-medium">{row.client_name}</TableCell>
                              <TableCell>{formatDate(row.investment_date)}</TableCell>
                              <TableCell className="max-w-[200px] truncate" title={row.bond_name}>{row.bond_name}</TableCell>
                              <TableCell><span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: row.type === 'Holding' ? colors.light_bg : `${colors.accent}20`, color: row.type === 'Holding' ? colors.primary : colors.accent }}>{row.type}</span></TableCell>
                              <TableCell className="text-right">{row.net_quantity}</TableCell>
                              <TableCell className="text-right font-semibold" style={{ color: colors.primary }}>{formatCurrency(row.net_face_value)}</TableCell>
                              <TableCell className="text-center">{row.days_held}</TableCell>
                              <TableCell className="text-right font-semibold" style={{ color: colors.accent }}>{formatCurrency(row.commission_amount)}</TableCell>
                            </TableRow>
                          ))}
                          {commissionRows.length === 0 && (
                            <TableRow><TableCell colSpan={8} className="text-center py-8" style={{ color: colors.text_secondary }}>Select a month and year, then click "View Report"</TableCell></TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
              <TabsContent value="payout">
                <Card className="border-0 shadow-sm">
                  <CardHeader className="flex flex-row items-center justify-between border-b" style={{ backgroundColor: colors.card_bg }}>
                    <CardTitle className="text-lg font-heading flex items-center gap-2" style={{ color: colors.primary }}><CreditCard className="h-5 w-5" />Payout History</CardTitle>
                    <Button variant="outline" size="sm" style={{ borderColor: colors.primary, color: colors.primary }} onClick={fetchPayout} disabled={loadingPayout} data-testid="partner-refresh-payout">
                      {loadingPayout ? <span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full inline-block" /> : <Search className="h-4 w-4 mr-1" />}
                      Refresh
                    </Button>
                  </CardHeader>
                  <CardContent className="p-0 overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow style={{ backgroundColor: `${colors.light_bg}50` }}>
                          <TableHead className="font-semibold" style={{ color: colors.primary }}>Month-Year</TableHead>
                          <TableHead className="font-semibold text-right" style={{ color: colors.primary }}>Total Commission</TableHead>
                          <TableHead className="font-semibold text-right" style={{ color: colors.primary }}>TDS</TableHead>
                          <TableHead className="font-semibold text-right" style={{ color: colors.primary }}>Net Payout</TableHead>
                          {user?.role !== 'subpartner' && <TableHead className="font-semibold text-center" style={{ color: colors.primary }}>Payout Date</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {payoutRows.map((row, idx) => (
                          <TableRow key={`${row.month}-${row.year}`} className="animate-fadeIn" style={{ animationDelay: `${idx * 0.05}s` }}>
                            <TableCell className="font-medium">{row.month_name} {row.year}</TableCell>
                            <TableCell className="text-right font-semibold" style={{ color: colors.primary }}>{formatCurrency(row.total_commission)}</TableCell>
                            <TableCell className="text-right">{formatCurrency(row.tds)}</TableCell>
                            <TableCell className="text-right font-semibold" style={{ color: colors.accent }}>{formatCurrency(row.net_payout)}</TableCell>
                            {user?.role !== 'subpartner' && <TableCell className="text-center">{row.payout_date || <span className="text-gray-400 italic">—</span>}</TableCell>}
                          </TableRow>
                        ))}
                        {payoutRows.length === 0 && (
                          <TableRow><TableCell colSpan={user?.role === 'subpartner' ? 4 : 5} className="text-center py-8" style={{ color: colors.text_secondary }}>No payout records available yet</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>
              <TabsContent value="user-logins">
                <Card className="border-0 shadow-sm">
                  <CardHeader className="flex flex-row items-center justify-between border-b" style={{ backgroundColor: colors.card_bg }}>
                    <CardTitle className="text-lg font-heading flex items-center gap-2" style={{ color: colors.primary }}><UserPlus className="h-5 w-5" />User Logins</CardTitle>
                    <Button size="sm" style={{ backgroundColor: colors.primary }} className="text-white" onClick={() => setShowAddUser(true)} data-testid="partner-add-user-btn"><Plus className="h-4 w-4 mr-1" />Add User Login</Button>
                  </CardHeader>
                  <CardContent className="p-0 overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow style={{ backgroundColor: `${colors.light_bg}50` }}>
                          <TableHead className="font-semibold" style={{ color: colors.primary }}>Username</TableHead>
                          <TableHead className="font-semibold" style={{ color: colors.primary }}>Role</TableHead>
                          <TableHead className="font-semibold" style={{ color: colors.primary }}>Client Name</TableHead>
                          <TableHead className="font-semibold text-center" style={{ color: colors.primary }}>Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {userLogins.map((u, idx) => (
                          <TableRow key={u.id || u.username} className="animate-fadeIn" style={{ animationDelay: `${idx * 0.05}s`, backgroundColor: u.is_self ? `${colors.light_bg}40` : undefined }}>
                            <TableCell className="font-medium">{u.username}{u.is_self && <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ backgroundColor: colors.primary, color: 'white' }}>You</span>}</TableCell>
                            <TableCell><span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: colors.light_bg, color: colors.primary }}>{u.role}</span></TableCell>
                            <TableCell>{u.entity_name}</TableCell>
                            <TableCell className="text-center">
                              <Button variant="ghost" size="sm" onClick={() => { setEditUser({ id: u.id, username: u.username, password: '', entity_name: u.entity_name, is_self: !!u.is_self }); setShowEditUser(true); }} data-testid={`edit-user-${u.id}`}><Pencil className="h-4 w-4" /></Button>
                            </TableCell>
                          </TableRow>
                        ))}
                        {userLogins.length === 0 && (
                          <TableRow><TableCell colSpan={4} className="text-center py-8" style={{ color: colors.text_secondary }}>{loadingUsers ? 'Loading...' : 'No user logins found'}</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>
              {/* SubPartners Tab */}
              {partnerConfig.allow_subpartner && user?.role === 'partner' && (
              <TabsContent value="subpartners">
                <Card className="border-0 shadow-sm">
                  <CardHeader className="flex flex-row items-center justify-between border-b" style={{ backgroundColor: colors.card_bg }}>
                    <CardTitle className="text-lg font-heading flex items-center gap-2" style={{ color: colors.primary }}><Users className="h-5 w-5" />SubPartners</CardTitle>
                    <Button size="sm" style={{ backgroundColor: colors.primary }} className="text-white" onClick={openAddSubpartner} data-testid="partner-add-subpartner-btn"><Plus className="h-4 w-4 mr-1" />Add SubPartner</Button>
                  </CardHeader>
                  <CardContent className="p-0 overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow style={{ backgroundColor: `${colors.light_bg}50` }}>
                          <TableHead className="font-semibold" style={{ color: colors.primary }}>Code</TableHead>
                          <TableHead className="font-semibold" style={{ color: colors.primary }}>Name</TableHead>
                          <TableHead className="font-semibold" style={{ color: colors.primary }}>Email</TableHead>
                          <TableHead className="font-semibold" style={{ color: colors.primary }}>Mobile</TableHead>
                          <TableHead className="font-semibold text-center" style={{ color: colors.primary }}>Share (%)</TableHead>
                          <TableHead className="font-semibold text-center" style={{ color: colors.primary }}>Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {subpartners.map((sp, idx) => (
                          <TableRow key={sp.subpartner_code} className="animate-fadeIn" style={{ animationDelay: `${idx * 0.05}s` }}>
                            <TableCell className="font-mono text-sm">{sp.subpartner_code}</TableCell>
                            <TableCell className="font-medium">{sp.subpartner_name}</TableCell>
                            <TableCell>{sp.email || '-'}</TableCell>
                            <TableCell>{sp.mobile || '-'}</TableCell>
                            <TableCell className="text-center font-medium">{sp.subpartner_share}%</TableCell>
                            <TableCell className="text-center">
                              <Button variant="ghost" size="sm" onClick={() => openEditSubpartner(sp)} data-testid={`edit-sp-${sp.subpartner_code}`}><Pencil className="h-4 w-4" /></Button>
                            </TableCell>
                          </TableRow>
                        ))}
                        {subpartners.length === 0 && (
                          <TableRow><TableCell colSpan={6} className="text-center py-8" style={{ color: colors.text_secondary }}>{loadingSubpartners ? 'Loading...' : 'No subpartners yet'}</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>
              )}
              {/* SubPartner Clients Tab */}
              {partnerConfig.allow_subpartner && user?.role === 'partner' && (
              <TabsContent value="sp-clients">
                <Card className="border-0 shadow-sm">
                  <CardHeader className="flex flex-row items-center justify-between border-b" style={{ backgroundColor: colors.card_bg }}>
                    <CardTitle className="text-lg font-heading flex items-center gap-2" style={{ color: colors.primary }}><Building2 className="h-5 w-5" />SubPartner Clients</CardTitle>
                    <div className="flex items-center gap-2">
                      <Label className="text-sm" style={{ color: colors.text_secondary }}>Filter:</Label>
                      <Select value={spClientFilter} onValueChange={setSpClientFilter}>
                        <SelectTrigger className="w-[180px]" data-testid="sp-client-filter">
                          <SelectValue placeholder="All SubPartners" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All SubPartners</SelectItem>
                          {subpartners.map(sp => (
                            <SelectItem key={sp.subpartner_code} value={sp.subpartner_code}>{sp.subpartner_name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0 overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow style={{ backgroundColor: `${colors.light_bg}50` }}>
                          <TableHead className="font-semibold" style={{ color: colors.primary }}>Client Name</TableHead>
                          <TableHead className="font-semibold" style={{ color: colors.primary }}>SubPartner</TableHead>
                          <TableHead className="font-semibold text-right" style={{ color: colors.primary }}>Total Invested Value</TableHead>
                          <TableHead className="font-semibold text-center" style={{ color: colors.primary }}>Action</TableHead>
                          <TableHead className="font-semibold" style={{ color: colors.primary }}>Email</TableHead>
                          <TableHead className="font-semibold" style={{ color: colors.primary }}>Mobile</TableHead>
                          <TableHead className="font-semibold text-center" style={{ color: colors.primary }}>CMR</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredSpClients.map((c, idx) => (
                          <TableRow key={c.client_id} className="animate-fadeIn" style={{ animationDelay: `${idx * 0.03}s` }}>
                            <TableCell className="font-medium">{c.client_name}</TableCell>
                            <TableCell><span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: colors.light_bg, color: colors.primary }}>{c.subpartner_name}</span></TableCell>
                            <TableCell className="text-right font-semibold" style={{ color: colors.accent }}>{formatCurrency(c.total_invested_value)}</TableCell>
                            <TableCell className="text-center">
                              <div className="flex items-center justify-center gap-1">
                                <Button variant="ghost" size="sm" onClick={() => openEditClient(c)} data-testid={`edit-sp-client-${c.client_id}`}><Pencil className="h-4 w-4" /></Button>
                                <Button size="sm" style={{ backgroundColor: colors.primary }} className="text-white" onClick={() => viewClientPortfolio(c)} data-testid={`view-sp-client-${c.client_id}`}><Search className="h-4 w-4 mr-2" />View Portfolio</Button>
                              </div>
                            </TableCell>
                            <TableCell>{c.email || '-'}</TableCell>
                            <TableCell>{c.mobile || '-'}</TableCell>
                            <TableCell className="text-center">{c.cmr_path ? <Button variant="ghost" size="sm" className="text-red-600" onClick={async () => { try { const res = await axios.get(`${API_URL}/api/download-cmr/${c.client_id}`, { ...getAuthHeaders(), responseType: 'blob' }); const url = window.URL.createObjectURL(res.data); const a = document.createElement('a'); a.href = url; a.download = c.cmr_filename || 'cmr.pdf'; a.click(); window.URL.revokeObjectURL(url); } catch { toast.error('Failed to download CMR'); } }} data-testid={`download-sp-cmr-${c.client_id}`}><FileDown className="h-4 w-4" /></Button> : <span className="text-gray-400 text-xs">-</span>}</TableCell>
                          </TableRow>
                        ))}
                        {filteredSpClients.length === 0 && (
                          <TableRow><TableCell colSpan={7} className="text-center py-8" style={{ color: colors.text_secondary }}>{loadingSpClients ? 'Loading...' : 'No subpartner clients found'}</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>
              )}
              {/* SubBroker Commission Tab */}
              {partnerConfig.allow_subpartner && user?.role === 'partner' && (
              <TabsContent value="sb-commission">
                <Card className="border-0 shadow-sm mb-6">
                  <CardHeader className="border-b" style={{ backgroundColor: colors.card_bg }}>
                    <CardTitle className="text-lg font-heading flex items-center gap-2" style={{ color: colors.primary }}><Calculator className="h-5 w-5" />SubBroker Commission Report</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="flex flex-wrap items-end gap-4">
                      <div className="w-44">
                        <label className="text-sm font-medium mb-1 block" style={{ color: colors.text_secondary }}>Month</label>
                        <Select value={String(sbCommMonth)} onValueChange={(v) => setSbCommMonth(Number(v))}>
                          <SelectTrigger data-testid="sb-commission-month"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {monthOptions.map(m => <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="w-28">
                        <label className="text-sm font-medium mb-1 block" style={{ color: colors.text_secondary }}>Year</label>
                        <input type="number" value={sbCommYear} onChange={(e) => setSbCommYear(Number(e.target.value))} min={2000} max={2099} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="sb-commission-year" />
                      </div>
                      <Button style={{ backgroundColor: colors.primary }} className="text-white" onClick={fetchSbCommission} disabled={loadingSbComm} data-testid="sb-fetch-commission-btn">
                        {loadingSbComm ? <span className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full inline-block" /> : <Search className="h-4 w-4 mr-2" />}
                        View Report
                      </Button>
                    </div>
                  </CardContent>
                </Card>
                {sbCommMeta && (
                  <Card className="border-0 shadow-sm">
                    <CardHeader className="flex flex-row items-center justify-between border-b" style={{ backgroundColor: `${colors.light_bg}80` }}>
                      <div>
                        <CardTitle className="text-lg font-heading" style={{ color: colors.primary }}>SubBroker Commission: {sbCommMeta.month_year}</CardTitle>
                      </div>
                      <div className="text-right">
                        <p className="text-sm" style={{ color: colors.text_secondary }}>Total SubBroker Commission</p>
                        <p className="text-2xl font-bold" style={{ color: colors.primary }}>{formatCurrency(sbCommTotal)}</p>
                      </div>
                    </CardHeader>
                    <CardContent className="p-0 overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow style={{ backgroundColor: `${colors.light_bg}50` }}>
                            <TableHead className="font-semibold" style={{ color: colors.primary }}>SubBroker</TableHead>
                            <TableHead className="font-semibold" style={{ color: colors.primary }}>Client Name</TableHead>
                            <TableHead className="font-semibold" style={{ color: colors.primary }}>Bond Name</TableHead>
                            <TableHead className="font-semibold" style={{ color: colors.primary }}>Type</TableHead>
                            <TableHead className="font-semibold text-right" style={{ color: colors.primary }}>Net Face Value</TableHead>
                            <TableHead className="font-semibold text-center" style={{ color: colors.primary }}>Days Held</TableHead>
                            <TableHead className="font-semibold text-right" style={{ color: colors.primary }}>Share %</TableHead>
                            <TableHead className="font-semibold text-right" style={{ color: colors.primary }}>Partner Commn</TableHead>
                            <TableHead className="font-semibold text-right" style={{ color: colors.accent }}>SB Commn</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {sbCommRows.map((row, idx) => (
                            <TableRow key={`${row.subpartner_code}-${row.client_id}-${row.bond_isin}-${row.type}-${idx}`} className="animate-fadeIn" style={{ animationDelay: `${idx * 0.03}s` }}>
                              <TableCell><span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: colors.light_bg, color: colors.primary }}>{row.subpartner_name}</span></TableCell>
                              <TableCell className="font-medium">{row.client_name}</TableCell>
                              <TableCell className="max-w-[180px] truncate" title={row.bond_name}>{row.bond_name}</TableCell>
                              <TableCell><span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: row.type === 'Holding' ? colors.light_bg : `${colors.accent}20`, color: row.type === 'Holding' ? colors.primary : colors.accent }}>{row.type}</span></TableCell>
                              <TableCell className="text-right font-semibold" style={{ color: colors.primary }}>{formatCurrency(row.net_face_value)}</TableCell>
                              <TableCell className="text-center">{row.days_held}</TableCell>
                              <TableCell className="text-right">{row.subpartner_share_pct}%</TableCell>
                              <TableCell className="text-right">{formatCurrency(row.partner_commission)}</TableCell>
                              <TableCell className="text-right font-semibold" style={{ color: colors.accent }}>{formatCurrency(row.subpartner_commission)}</TableCell>
                            </TableRow>
                          ))}
                          {sbCommRows.length === 0 && (
                            <TableRow><TableCell colSpan={9} className="text-center py-8" style={{ color: colors.text_secondary }}>Select a month and year, then click "View Report"</TableCell></TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
              )}
              {/* SubBroker Payout Tab */}
              {partnerConfig.allow_subpartner && user?.role === 'partner' && (
              <TabsContent value="sb-payout">
                <Card className="border-0 shadow-sm">
                  <CardHeader className="border-b" style={{ backgroundColor: colors.card_bg }}>
                    <CardTitle className="text-lg font-heading flex items-center gap-2" style={{ color: colors.primary }}><CreditCard className="h-5 w-5" />SubBroker Payout Report</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="flex flex-wrap items-end gap-4">
                      <div className="w-44">
                        <label className="text-sm font-medium mb-1 block" style={{ color: colors.text_secondary }}>Month</label>
                        <Select value={String(sbPayoutMonth)} onValueChange={(v) => setSbPayoutMonth(Number(v))}>
                          <SelectTrigger data-testid="sb-payout-month"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {monthOptions.map(m => <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="w-28">
                        <label className="text-sm font-medium mb-1 block" style={{ color: colors.text_secondary }}>Year</label>
                        <input type="number" value={sbPayoutYear} onChange={(e) => setSbPayoutYear(Number(e.target.value))} min={2000} max={2099} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="sb-payout-year" />
                      </div>
                      <Button style={{ backgroundColor: colors.primary }} className="text-white" onClick={fetchSbPayout} disabled={loadingSbPayout} data-testid="sb-fetch-payout-btn">
                        {loadingSbPayout ? <span className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full inline-block" /> : <Search className="h-4 w-4 mr-2" />}
                        View Payout
                      </Button>
                    </div>
                  </CardContent>
                </Card>
                {sbPayoutRows.length > 0 && (
                  <Card className="border-0 shadow-sm mt-4">
                    <CardHeader className="flex flex-row items-center justify-between border-b" style={{ backgroundColor: `${colors.light_bg}80` }}>
                      <CardTitle className="text-lg font-heading" style={{ color: colors.primary }}>SubBroker Payout: {sbPayoutRows[0]?.month_year}</CardTitle>
                      <div className="text-right">
                        <p className="text-sm" style={{ color: colors.text_secondary }}>Total Net Payout</p>
                        <p className="text-2xl font-bold" style={{ color: colors.accent }}>{formatCurrency(sbPayoutRows.reduce((s, r) => s + r.net_payout, 0))}</p>
                      </div>
                    </CardHeader>
                    <CardContent className="p-0 overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow style={{ backgroundColor: `${colors.light_bg}50` }}>
                            <TableHead className="font-semibold" style={{ color: colors.primary }}>SubBroker Code</TableHead>
                            <TableHead className="font-semibold" style={{ color: colors.primary }}>SubBroker Name</TableHead>
                            <TableHead className="font-semibold text-right" style={{ color: colors.primary }}>Share %</TableHead>
                            <TableHead className="font-semibold text-right" style={{ color: colors.primary }}>Partner Commission</TableHead>
                            <TableHead className="font-semibold text-right" style={{ color: colors.primary }}>SB Commission</TableHead>
                            <TableHead className="font-semibold text-right" style={{ color: colors.primary }}>TDS (2%)</TableHead>
                            <TableHead className="font-semibold text-right" style={{ color: colors.accent }}>Net Payout</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {sbPayoutRows.map((row, idx) => (
                            <TableRow key={row.subpartner_code} className="animate-fadeIn" style={{ animationDelay: `${idx * 0.05}s` }}>
                              <TableCell className="font-mono text-sm">{row.subpartner_code}</TableCell>
                              <TableCell className="font-medium">{row.subpartner_name}</TableCell>
                              <TableCell className="text-right">{row.subpartner_share_pct}%</TableCell>
                              <TableCell className="text-right">{formatCurrency(row.partner_commission)}</TableCell>
                              <TableCell className="text-right font-semibold" style={{ color: colors.primary }}>{formatCurrency(row.subpartner_commission)}</TableCell>
                              <TableCell className="text-right">{formatCurrency(row.tds)}</TableCell>
                              <TableCell className="text-right font-semibold" style={{ color: colors.accent }}>{formatCurrency(row.net_payout)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
              )}
              {/* My Bond Commission Tab */}
              {partnerConfig.allow_my_bond_commission && user?.role === 'partner' && (
              <TabsContent value="mb-commission">
                <Card className="border-0 shadow-sm mb-6">
                  <CardHeader className="border-b" style={{ backgroundColor: colors.card_bg }}>
                    <CardTitle className="text-lg font-heading flex items-center gap-2" style={{ color: colors.primary }}><Calculator className="h-5 w-5" />My Bond Commission</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="flex flex-wrap items-end gap-4">
                      <div className="w-44">
                        <label className="text-sm font-medium mb-1 block" style={{ color: colors.text_secondary }}>Month</label>
                        <Select value={String(mbCommMonth)} onValueChange={(v) => setMbCommMonth(Number(v))}>
                          <SelectTrigger data-testid="mb-commission-month"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {monthOptions.map(m => <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="w-28">
                        <label className="text-sm font-medium mb-1 block" style={{ color: colors.text_secondary }}>Year</label>
                        <input type="number" value={mbCommYear} onChange={(e) => setMbCommYear(Number(e.target.value))} min={2000} max={2099} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="mb-commission-year" />
                      </div>
                      <Button style={{ backgroundColor: colors.primary }} className="text-white" onClick={computeMyBondCommission} disabled={loadingMbComm} data-testid="mb-compute-commission-btn">
                        {loadingMbComm ? <span className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full inline-block" /> : <Calculator className="h-4 w-4 mr-2" />}
                        Compute Commission
                      </Button>
                      <Button variant="outline" style={{ borderColor: colors.primary, color: colors.primary }} onClick={fetchMyBondCommission} disabled={loadingMbComm} data-testid="mb-view-commission-btn">
                        <Search className="h-4 w-4 mr-2" />View Saved
                      </Button>
                    </div>
                  </CardContent>
                </Card>
                {mbCommMeta && (
                  <Card className="border-0 shadow-sm">
                    <CardHeader className="flex flex-row items-center justify-between border-b" style={{ backgroundColor: `${colors.light_bg}80` }}>
                      <div>
                        <CardTitle className="text-lg font-heading" style={{ color: colors.primary }}>My Bond Commission: {mbCommMeta.month_year}</CardTitle>
                        <p className="text-sm mt-1" style={{ color: colors.text_secondary }}>{mbCommMeta.partner_name}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm" style={{ color: colors.text_secondary }}>Total Commission</p>
                        <p className="text-2xl font-bold" style={{ color: colors.primary }}>{formatCurrency(mbCommTotal)}</p>
                      </div>
                    </CardHeader>
                    <CardContent className="p-0 overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow style={{ backgroundColor: `${colors.light_bg}50` }}>
                            <TableHead className="font-semibold" style={{ color: colors.primary }}>Client Name</TableHead>
                            <TableHead className="font-semibold" style={{ color: colors.primary }}>Bond Name</TableHead>
                            <TableHead className="font-semibold" style={{ color: colors.primary }}>Type</TableHead>
                            <TableHead className="font-semibold text-right" style={{ color: colors.primary }}>Net Face Value</TableHead>
                            <TableHead className="font-semibold text-center" style={{ color: colors.primary }}>Days</TableHead>
                            <TableHead className="font-semibold text-right" style={{ color: colors.primary }}>Partner Share</TableHead>
                            <TableHead className="font-semibold text-right" style={{ color: colors.accent }}>Commission</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {mbCommRows.map((row, idx) => (
                            <TableRow key={`${row.client_id}-${row.bond_isin}-${row.type}-${idx}`} className="animate-fadeIn" style={{ animationDelay: `${idx * 0.03}s` }}>
                              <TableCell className="font-medium">{row.client_name}</TableCell>
                              <TableCell className="max-w-[200px] truncate" title={row.bond_name}>{row.bond_name}</TableCell>
                              <TableCell><span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: row.type === 'Holding' ? colors.light_bg : `${colors.accent}20`, color: row.type === 'Holding' ? colors.primary : colors.accent }}>{row.type}</span></TableCell>
                              <TableCell className="text-right font-semibold" style={{ color: colors.primary }}>{formatCurrency(row.net_face_value)}</TableCell>
                              <TableCell className="text-center">{row.days_held}</TableCell>
                              <TableCell className="text-right">{row.partner_share ? `${(row.partner_share * 100).toFixed(2)}%` : '—'}</TableCell>
                              <TableCell className="text-right font-semibold" style={{ color: colors.accent }}>{formatCurrency(row.commission_amount)}</TableCell>
                            </TableRow>
                          ))}
                          {mbCommRows.length === 0 && (
                            <TableRow><TableCell colSpan={7} className="text-center py-8" style={{ color: colors.text_secondary }}>Select a month and year, then click "Compute Commission"</TableCell></TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
              )}
              {/* My Bond Payout Tab */}
              {partnerConfig.allow_my_bond_commission && user?.role === 'partner' && (
              <TabsContent value="mb-payout">
                <Card className="border-0 shadow-sm mb-6">
                  <CardHeader className="border-b" style={{ backgroundColor: colors.card_bg }}>
                    <CardTitle className="text-lg font-heading flex items-center gap-2" style={{ color: colors.primary }}><CreditCard className="h-5 w-5" />My Bond Payout</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="flex flex-wrap items-end gap-4">
                      <div className="w-44">
                        <label className="text-sm font-medium mb-1 block" style={{ color: colors.text_secondary }}>Generate payout for</label>
                        <Select value={String(mbCommMonth)} onValueChange={(v) => setMbCommMonth(Number(v))}>
                          <SelectTrigger data-testid="mb-payout-month"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {monthOptions.map(m => <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="w-28">
                        <label className="text-sm font-medium mb-1 block" style={{ color: colors.text_secondary }}>Year</label>
                        <input type="number" value={mbCommYear} onChange={(e) => setMbCommYear(Number(e.target.value))} min={2000} max={2099} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="mb-payout-year" />
                      </div>
                      <Button style={{ backgroundColor: colors.accent }} className="text-white" onClick={generateMyBondPayout} disabled={loadingMbPayout} data-testid="mb-generate-payout-btn">
                        {loadingMbPayout ? <span className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full inline-block" /> : <CreditCard className="h-4 w-4 mr-2" />}
                        Generate Payout
                      </Button>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-sm">
                  <CardHeader className="border-b" style={{ backgroundColor: `${colors.light_bg}80` }}>
                    <CardTitle className="text-lg font-heading" style={{ color: colors.primary }}>My Bond Payout History</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0 overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow style={{ backgroundColor: `${colors.light_bg}50` }}>
                          <TableHead className="font-semibold" style={{ color: colors.primary }}>Month</TableHead>
                          <TableHead className="font-semibold text-right" style={{ color: colors.primary }}>Total Commission</TableHead>
                          <TableHead className="font-semibold text-right" style={{ color: colors.primary }}>TDS (2%)</TableHead>
                          <TableHead className="font-semibold text-right" style={{ color: colors.primary }}>Net Payout</TableHead>
                          <TableHead className="font-semibold text-center" style={{ color: colors.primary }}>Payout Date</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {mbPayoutRows.map((row, idx) => (
                          <TableRow key={`${row.month}-${row.year}`} className="animate-fadeIn" style={{ animationDelay: `${idx * 0.05}s` }}>
                            <TableCell className="font-medium">{row.month_name} {row.year}</TableCell>
                            <TableCell className="text-right font-semibold" style={{ color: colors.primary }}>{formatCurrency(row.total_commission)}</TableCell>
                            <TableCell className="text-right">{formatCurrency(row.tds)}</TableCell>
                            <TableCell className="text-right font-semibold" style={{ color: colors.accent }}>{formatCurrency(row.net_payout)}</TableCell>
                            <TableCell className="text-center">{row.payout_date || <span className="text-gray-400 italic">—</span>}</TableCell>
                          </TableRow>
                        ))}
                        {mbPayoutRows.length === 0 && (
                          <TableRow><TableCell colSpan={5} className="text-center py-8" style={{ color: colors.text_secondary }}>{loadingMbPayout ? 'Loading...' : 'No My Bond payout records yet'}</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>
              )}
              {/* My Bonds Tab */}
              {partnerConfig.allow_my_bonds && user?.role === 'partner' && (
              <TabsContent value="my-bonds">
                <Card className="border-0 shadow-sm">
                  <CardHeader className="flex flex-row items-center justify-between border-b" style={{ backgroundColor: colors.card_bg }}>
                    <CardTitle className="text-lg font-heading flex items-center gap-2" style={{ color: colors.primary }}><FileText className="h-5 w-5" />My Bonds</CardTitle>
                    <Button size="sm" style={{ backgroundColor: colors.accent }} className="text-white" onClick={() => openMyBondModal()} data-testid="partner-add-mybond-btn"><Plus className="h-4 w-4 mr-1" />Add Bond</Button>
                  </CardHeader>
                  <CardContent className="p-0 overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow style={{ backgroundColor: `${colors.light_bg}50` }}>
                          <TableHead className="font-semibold" style={{ color: colors.primary }}>ISIN</TableHead>
                          <TableHead className="font-semibold" style={{ color: colors.primary }}>Bond Name</TableHead>
                          <TableHead className="font-semibold" style={{ color: colors.primary }}>Rating</TableHead>
                          <TableHead className="font-semibold text-right" style={{ color: colors.primary }}>Yield (%)</TableHead>
                          <TableHead className="font-semibold" style={{ color: colors.primary }}>Maturity</TableHead>
                          <TableHead className="font-semibold" style={{ color: colors.primary }}>Payout Freq</TableHead>
                          <TableHead className="font-semibold text-center" style={{ color: colors.primary }}>Available</TableHead>
                          <TableHead className="font-semibold text-center" style={{ color: colors.primary }}>Image</TableHead>
                          <TableHead className="font-semibold text-center" style={{ color: colors.primary }}>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {myBonds.map((bond, idx) => (
                          <TableRow key={bond.bond_isin} className="animate-fadeIn" style={{ animationDelay: `${idx * 0.03}s` }}>
                            <TableCell className="font-mono text-xs">{bond.bond_isin}</TableCell>
                            <TableCell className="font-medium">{bond.bond_name}</TableCell>
                            <TableCell>{bond.rating || '-'}</TableCell>
                            <TableCell className="text-right" style={{ color: colors.accent }}>{bond.yield_rate ? `${(bond.yield_rate * 100).toFixed(2)}%` : '-'}</TableCell>
                            <TableCell>{(bond.maturity_pct_2 || bond.maturity_pct_3) ? <MaturityBadge maturityDate={bond.maturity_date} maturityPct1={bond.maturity_pct_1} maturityDate2={bond.maturity_date_2} maturityPct2={bond.maturity_pct_2} maturityDate3={bond.maturity_date_3} maturityPct3={bond.maturity_pct_3} /> : (bond.maturity_date || '-')}</TableCell>
                            <TableCell>{bond.interest_payout_frequency || '-'}</TableCell>
                            <TableCell className="text-center">{bond.available ? <CheckCircle className="h-4 w-4 text-green-600 inline" /> : <AlertTriangle className="h-4 w-4 text-red-500 inline" />}</TableCell>
                            <TableCell className="text-center">
                              {bond.bond_image_url ? (
                                <a href={bond.bond_image_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs flex items-center justify-center gap-1"><ImageIcon className="h-3 w-3" />View</a>
                              ) : '-'}
                            </TableCell>
                            <TableCell className="text-center">
                              <Button variant="ghost" size="sm" onClick={() => openMyBondModal(bond)} data-testid={`edit-mybond-${bond.bond_isin}`}><Pencil className="h-4 w-4" /></Button>
                              <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteMyBond(bond.bond_isin)} data-testid={`delete-mybond-${bond.bond_isin}`}><Trash2 className="h-4 w-4" /></Button>
                            </TableCell>
                          </TableRow>
                        ))}
                        {myBonds.length === 0 && (
                          <TableRow><TableCell colSpan={9} className="text-center py-8" style={{ color: colors.text_secondary }}>{loadingMyBonds ? 'Loading...' : 'No bonds added yet. Click "Add Bond" to get started.'}</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>
              )}

              <TabsContent value="families">
                <Card className="shadow-md">
                  <CardHeader className="text-white rounded-t-lg flex flex-row items-center justify-between" style={{ background: `linear-gradient(to right, ${colors.primary}, ${colors.primary}dd)` }}>
                    <CardTitle className="font-heading flex items-center gap-2"><Home className="h-5 w-5" />Family Management</CardTitle>
                    <Button size="sm" className="bg-white hover:bg-gray-100" style={{ color: colors.primary }} data-testid="partner-add-family-btn" onClick={() => {
                      setEditingFamily(null);
                      setFamilyForm({ family_name: '', head_client_id: '', member_client_ids: [] });
                      setShowFamilyModal(true);
                    }}><Plus className="h-4 w-4 mr-1" />Add Family</Button>
                  </CardHeader>
                  <CardContent className="p-0 overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow style={{ backgroundColor: `${colors.light_bg}80` }}>
                          <TableHead>Family ID</TableHead>
                          <TableHead>Family Name</TableHead>
                          <TableHead>Head</TableHead>
                          <TableHead className="text-center">Members</TableHead>
                          <TableHead className="text-center">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {families.map((fam) => (
                          <TableRow key={fam.family_id}>
                            <TableCell className="font-mono text-xs">{fam.family_id}</TableCell>
                            <TableCell className="font-medium">{fam.family_name}</TableCell>
                            <TableCell>{fam.head_client_name}</TableCell>
                            <TableCell className="text-center">{fam.members?.length || 0}</TableCell>
                            <TableCell className="text-center">
                              <Button variant="ghost" size="sm" onClick={() => {
                                setEditingFamily(fam);
                                setFamilyForm({
                                  family_name: fam.family_name,
                                  head_client_id: fam.head_client_id,
                                  member_client_ids: fam.members?.filter(m => m.client_id !== fam.head_client_id).map(m => m.client_id) || []
                                });
                                setShowFamilyModal(true);
                              }}><Pencil className="h-4 w-4" /></Button>
                              <Button variant="ghost" size="sm" className="text-red-500" onClick={() => requestDelete({
                                title: 'Delete Family',
                                description: 'Delete this family? Client data will not be affected.',
                                itemLabel: fam.family_name || fam.family_id,
                                onConfirmed: async () => {
                                  await axios.delete(`${API_URL}/api/partner/families/${fam.family_id}`, getAuthHeaders());
                                  toast.success('Family deleted');
                                  fetchFamilies();
                                },
                              })}><Trash2 className="h-4 w-4" /></Button>
                            </TableCell>
                          </TableRow>
                        ))}
                        {families.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-gray-400">No families created yet</TableCell></TableRow>}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Maturity Report */}
              <TabsContent value="maturity-report">
                <Card className="holdings-card border-0" data-testid="maturity-report">
                  <CardHeader className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b" style={{ backgroundColor: colors.card_bg }}>
                    <CardTitle className="text-lg font-heading flex items-center gap-2" style={{ color: colors.primary }}><CalendarDays className="h-5 w-5" />Upcoming Maturity Report</CardTitle>
                    <div className="flex flex-wrap items-center gap-2">
                      {[{ val: '3m', label: '3 Months' }, { val: '6m', label: '6 Months' }, { val: '1y', label: '1 Year' }, { val: 'custom', label: 'Custom' }].map(opt => (
                        <Button key={opt.val} variant={maturityFilter === opt.val ? 'default' : 'outline'} size="sm"
                          style={maturityFilter === opt.val ? { backgroundColor: colors.primary, color: 'white' } : { borderColor: colors.primary, color: colors.primary }}
                          onClick={() => {
                            setMaturityFilter(opt.val);
                            if (opt.val !== 'custom') {
                              const td = getMaturityTillDate(opt.val, '');
                              if (td) fetchMaturityReport(td);
                            }
                          }}
                          data-testid={`maturity-filter-${opt.val}`}
                        >{opt.label}</Button>
                      ))}
                      {maturityFilter === 'custom' && (
                        <div className="flex items-center gap-2">
                          <Input type="date" value={maturityCustomDate} onChange={(e) => setMaturityCustomDate(e.target.value)} className="h-8 w-40 text-sm" data-testid="maturity-custom-date" />
                          <Button size="sm" style={{ backgroundColor: colors.accent }} className="text-white h-8" disabled={!maturityCustomDate}
                            onClick={() => fetchMaturityReport(maturityCustomDate)} data-testid="maturity-custom-go"
                          >Go</Button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="p-4">
                    {loadingMaturity ? (
                      <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-4 rounded-full" style={{ borderColor: `${colors.primary}30`, borderTopColor: colors.primary }} /></div>
                    ) : maturityReport.length === 0 ? (
                      <p className="text-center py-12 text-sm" style={{ color: colors.text_secondary }}>No upcoming maturities found for the selected period.</p>
                    ) : (
                      <div className="space-y-6">
                        {maturityReport.map(client => (
                          <div key={client.client_id} data-testid={`maturity-client-${client.client_id}`}>
                            <h3 className="text-sm font-bold mb-2 flex items-center gap-2" style={{ color: colors.primary }}>
                              <Building2 className="h-4 w-4" />{client.client_name}
                              <span className="text-xs font-normal px-2 py-0.5 rounded-full" style={{ backgroundColor: colors.light_bg, color: colors.text_secondary }}>{client.holdings.length} bond{client.holdings.length !== 1 ? 's' : ''}</span>
                            </h3>
                            <div className="overflow-x-auto rounded-lg border" style={{ borderColor: `${colors.primary}15` }}>
                              <Table>
                                <TableHeader>
                                  <TableRow style={{ backgroundColor: `${colors.light_bg}80` }}>
                                    <TableHead className="font-semibold text-xs" style={{ color: colors.primary }}>Bond ISIN</TableHead>
                                    <TableHead className="font-semibold text-xs" style={{ color: colors.primary }}>Bond Name</TableHead>
                                    <TableHead className="font-semibold text-xs text-right" style={{ color: colors.primary }}>Net Qty</TableHead>
                                    <TableHead className="font-semibold text-xs text-right" style={{ color: colors.primary }}>Invested Value</TableHead>
                                    <TableHead className="font-semibold text-xs text-center" style={{ color: colors.primary }}>Maturity Date</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {client.holdings.map(h => (
                                    <TableRow key={h.bond_isin}>
                                      <TableCell className="font-mono text-xs">{h.bond_isin}</TableCell>
                                      <TableCell className="font-medium text-sm">{h.bond_name}</TableCell>
                                      <TableCell className="text-right font-semibold" style={{ color: colors.primary }}>{h.net_quantity}</TableCell>
                                      <TableCell className="text-right font-semibold" style={{ color: colors.accent }}>{formatCurrency(h.invested_value)}</TableCell>
                                      <TableCell className="text-center"><MaturityBadge maturityDate={h.maturity_date} maturityPct1={h.maturity_pct_1} maturityDate2={h.maturity_date_2} maturityPct2={h.maturity_pct_2} maturityDate3={h.maturity_date_3} maturityPct3={h.maturity_pct_3} /></TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Bond Holdings Report */}
              <TabsContent value="bond-holdings">
                <Card className="holdings-card border-0" data-testid="bond-holdings-report">
                  <CardHeader className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b" style={{ backgroundColor: colors.card_bg }}>
                    <CardTitle className="text-lg font-heading flex items-center gap-2" style={{ color: colors.primary }}><Briefcase className="h-5 w-5" />Bond-wise Holdings Report</CardTitle>
                    <div className="flex items-center gap-2">
                      <Select value={bondHoldingsFilter} onValueChange={(v) => { if (v === '__all__') { setBondHoldingsFilter('__all__'); setBondClientBreakdown([]); } else { setBondHoldingsFilter(v); fetchBondHoldingsReport(v); } }}>
                        <SelectTrigger className="w-[320px] h-9 text-sm" data-testid="bond-holdings-filter"><SelectValue placeholder="All Bonds (Summary)" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__all__">All Bonds (Summary)</SelectItem>
                          {bondHoldingsData.map(b => <SelectItem key={b.bond_isin} value={b.bond_isin}>{b.bond_isin} - {b.bond_name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Button variant="outline" size="sm" style={{ borderColor: colors.primary, color: colors.primary }} onClick={() => { setBondHoldingsFilter('__all__'); setBondClientBreakdown([]); fetchBondHoldingsReport(); }} data-testid="bond-holdings-refresh">Refresh</Button>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    {loadingBondHoldings ? (
                      <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-4 rounded-full" style={{ borderColor: `${colors.primary}30`, borderTopColor: colors.primary }} /></div>
                    ) : bondHoldingsFilter !== '__all__' && bondClientBreakdown.length > 0 ? (
                      <>
                        <div className="px-4 pt-4 pb-2"><p className="text-sm font-medium" style={{ color: colors.primary }}>Client-wise breakdown for: <span className="font-bold">{bondClientBreakdown[0]?.bond_isin} - {bondClientBreakdown[0]?.bond_name}</span></p></div>
                        <Table>
                          <TableHeader><TableRow style={{ backgroundColor: `${colors.light_bg}80` }}>
                            <TableHead className="font-semibold" style={{ color: colors.primary }}>Client Name</TableHead>
                            <TableHead className="font-semibold text-right" style={{ color: colors.primary }}>Net Quantity</TableHead>
                            <TableHead className="font-semibold text-right" style={{ color: colors.primary }}>Invested Value</TableHead>
                            <TableHead className="font-semibold text-right" style={{ color: colors.primary }}>Avg Yield</TableHead>
                            <TableHead className="font-semibold text-center" style={{ color: colors.primary }}>Transactions</TableHead>
                          </TableRow></TableHeader>
                          <TableBody>
                            {bondClientBreakdown.map(row => (
                              <TableRow key={row.client_id}>
                                <TableCell className="font-medium">{row.client_name}</TableCell>
                                <TableCell className="text-right font-semibold" style={{ color: colors.primary }}>{row.net_quantity}</TableCell>
                                <TableCell className="text-right font-semibold" style={{ color: colors.accent }}>{formatCurrency(row.invested_value)}</TableCell>
                                <TableCell className="text-right">{(row.avg_yield * 100).toFixed(2)}%</TableCell>
                                <TableCell className="text-center"><button className="lens-btn" onClick={() => viewBhTransactions(row)} data-testid={`bh-txn-${row.client_id}`}><Search className="h-4 w-4" /></button></TableCell>
                              </TableRow>
                            ))}
                            <TableRow className="font-bold" style={{ backgroundColor: colors.light_bg }}>
                              <TableCell>Total ({bondClientBreakdown.length} clients)</TableCell>
                              <TableCell className="text-right">{bondClientBreakdown.reduce((s, r) => s + r.net_quantity, 0)}</TableCell>
                              <TableCell className="text-right">{formatCurrency(bondClientBreakdown.reduce((s, r) => s + r.invested_value, 0))}</TableCell>
                              <TableCell colSpan={2}></TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </>
                    ) : bondHoldingsFilter !== '__all__' && bondClientBreakdown.length === 0 && !loadingBondHoldings ? (
                      <p className="text-center py-12 text-sm" style={{ color: colors.text_secondary }}>No active holdings found for this bond.</p>
                    ) : bondHoldingsData.length > 0 ? (
                      <Table>
                        <TableHeader><TableRow style={{ backgroundColor: `${colors.light_bg}80` }}>
                          <TableHead className="font-semibold" style={{ color: colors.primary }}>Bond ISIN</TableHead>
                          <TableHead className="font-semibold" style={{ color: colors.primary }}>Bond Name</TableHead>
                          <TableHead className="font-semibold text-right" style={{ color: colors.primary }}>Net Quantity</TableHead>
                          <TableHead className="font-semibold text-right" style={{ color: colors.primary }}>Invested Value</TableHead>
                          <TableHead className="font-semibold text-center" style={{ color: colors.primary }}>Maturity</TableHead>
                        </TableRow></TableHeader>
                        <TableBody>
                          {bondHoldingsData.map(b => (
                            <TableRow key={b.bond_isin} className="cursor-pointer hover:bg-slate-50" onClick={() => { setBondHoldingsFilter(b.bond_isin); fetchBondHoldingsReport(b.bond_isin); }} data-testid={`bond-holding-row-${b.bond_isin}`}>
                              <TableCell className="font-mono text-sm">{b.bond_isin}</TableCell>
                              <TableCell className="font-medium">{b.bond_name}</TableCell>
                              <TableCell className="text-right font-semibold" style={{ color: colors.primary }}>{b.net_quantity}</TableCell>
                              <TableCell className="text-right font-semibold" style={{ color: colors.accent }}>{formatCurrency(b.invested_value)}</TableCell>
                              <TableCell className="text-center"><MaturityBadge maturityDate={b.maturity_date} maturityPct1={b.maturity_pct_1} maturityDate2={b.maturity_date_2} maturityPct2={b.maturity_pct_2} maturityDate3={b.maturity_date_3} maturityPct3={b.maturity_pct_3} /></TableCell>
                            </TableRow>
                          ))}
                          <TableRow className="font-bold" style={{ backgroundColor: colors.light_bg }}>
                            <TableCell colSpan={2}>Total ({bondHoldingsData.length} bonds)</TableCell>
                            <TableCell className="text-right">{bondHoldingsData.reduce((s, r) => s + r.net_quantity, 0)}</TableCell>
                            <TableCell className="text-right">{formatCurrency(bondHoldingsData.reduce((s, r) => s + r.invested_value, 0))}</TableCell>
                            <TableCell></TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    ) : (
                      <p className="text-center py-12 text-sm" style={{ color: colors.text_secondary }}>No active bond holdings found.</p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

            </Tabs>
          </>
        ) : (
          <>
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
              <div>
                <Button variant="ghost" size="sm" onClick={backToClients} className="mb-2" style={{ color: colors.text_secondary }} data-testid="back-to-clients-btn"><ArrowLeft className="h-4 w-4 mr-1" />Back to Clients</Button>
                <h1 className="text-2xl md:text-3xl font-heading font-bold mb-1" style={{ color: colors.text_primary }}>{selectedClient.client_name}</h1>
                <p style={{ color: colors.text_secondary }}>Client ID: {selectedClient.client_id}</p>
              </div>
              <div className="flex gap-2 mt-4 md:mt-0">
                <Button variant="outline" size="sm" style={{ borderColor: colors.primary, color: colors.primary }} onClick={exportToPDF} data-testid="export-pdf-btn"><FileText className="h-4 w-4 mr-2" />PDF</Button>
                <Button variant="outline" size="sm" style={{ borderColor: colors.accent, color: colors.accent }} onClick={exportToExcel} data-testid="export-excel-btn"><FileSpreadsheet className="h-4 w-4 mr-2" />Excel</Button>
              </div>
            </div>
            {clientSummary && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <Card className="summary-card border-0"><CardContent className="pt-6"><div className="flex items-center gap-4"><div className="p-3 rounded-lg" style={{ backgroundColor: colors.light_bg }}><Wallet className="h-6 w-6" style={{ color: colors.primary }} /></div><div><p className="text-sm" style={{ color: colors.text_secondary }}>Total Invested Value</p><p className="text-2xl font-bold" style={{ color: colors.primary }}>{formatCurrency(clientSummary.total_invested_value)}</p></div></div></CardContent></Card>
                <Card className="summary-card border-0"><CardContent className="pt-6"><div className="flex items-center gap-4"><div className="p-3 rounded-lg" style={{ backgroundColor: `${colors.accent}20` }}><Briefcase className="h-6 w-6" style={{ color: colors.accent }} /></div><div><p className="text-sm" style={{ color: colors.text_secondary }}>Active Holdings</p><p className="text-2xl font-bold" style={{ color: colors.accent }}>{clientSummary.total_holdings}</p></div></div></CardContent></Card>
              </div>
            )}
            <Tabs defaultValue="active" className="w-full">
              <TabsList className="tabs-scroll mb-4 bg-white border overflow-x-auto flex-nowrap w-full justify-start gap-1 p-1" style={{ display: 'flex', whiteSpace: 'nowrap' }}>
                <TabsTrigger value="active" style={{ '--tab-active-bg': colors.primary }}>Active Holdings ({clientHoldings.length})</TabsTrigger>
                <TabsTrigger value="redeemed" style={{ '--tab-active-bg': colors.accent }}>Fully Redeemed ({clientRedeemed.length})</TabsTrigger>
              </TabsList>
              <TabsContent value="active">
                <Card className="holdings-card border-0">
                  <CardHeader className="border-b" style={{ backgroundColor: colors.card_bg }}><CardTitle className="text-lg font-heading" style={{ color: colors.primary }}>Bond Portfolio Holdings</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    <Table className="holdings-table">
                      <TableHeader><TableRow style={{ backgroundColor: `${colors.light_bg}80` }}><TableHead className="font-semibold" style={{ color: colors.primary }}>Bond ISIN</TableHead><TableHead className="font-semibold" style={{ color: colors.primary }}>Bond Name</TableHead><TableHead className="font-semibold text-right" style={{ color: colors.primary }}>Net Quantity</TableHead><TableHead className="font-semibold text-right" style={{ color: colors.primary }}>Invested Value</TableHead><TableHead className="font-semibold text-center" style={{ color: colors.primary }}>Maturity Date</TableHead><TableHead className="font-semibold text-center" style={{ color: colors.primary }}>Transactions</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {clientHoldings.map((holding, idx) => (<TableRow key={holding.bond_isin} className="animate-fadeIn" style={{ animationDelay: `${idx * 0.05}s` }}><TableCell className="font-mono text-sm">{holding.bond_isin}</TableCell><TableCell className="font-medium">{holding.bond_name}</TableCell><TableCell className="text-right font-semibold" style={{ color: colors.primary }}>{holding.net_quantity}</TableCell><TableCell className="text-right font-semibold" style={{ color: colors.accent }}>{formatCurrency(holding.invested_value)}</TableCell><TableCell className="text-center"><MaturityBadge maturityDate={holding.maturity_date} maturityPct1={holding.maturity_pct_1} maturityDate2={holding.maturity_date_2} maturityPct2={holding.maturity_pct_2} maturityDate3={holding.maturity_date_3} maturityPct3={holding.maturity_pct_3} /></TableCell><TableCell className="text-center"><button className="lens-btn" onClick={() => viewTransactions(holding)}><Search className="h-4 w-4" /></button></TableCell></TableRow>))}
                        {clientHoldings.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8" style={{ color: colors.text_secondary }}>No active holdings</TableCell></TableRow>}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>
              <TabsContent value="redeemed">
                <Card className="holdings-card border-0">
                  <CardHeader className="border-b" style={{ backgroundColor: `${colors.accent}15` }}><CardTitle className="text-lg font-heading" style={{ color: colors.accent }}>Fully Redeemed Holdings</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    <Table className="holdings-table">
                      <TableHeader><TableRow style={{ backgroundColor: `${colors.accent}15` }}><TableHead className="font-semibold" style={{ color: colors.accent }}>Bond ISIN</TableHead><TableHead className="font-semibold" style={{ color: colors.accent }}>Bond Name</TableHead><TableHead className="font-semibold text-right" style={{ color: colors.accent }}>Net Quantity</TableHead><TableHead className="font-semibold text-center" style={{ color: colors.accent }}>Transactions</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {clientRedeemed.map((holding, idx) => (<TableRow key={holding.bond_isin} className="animate-fadeIn" style={{ animationDelay: `${idx * 0.05}s` }}><TableCell className="font-mono text-sm">{holding.bond_isin}</TableCell><TableCell className="font-medium">{holding.bond_name}</TableCell><TableCell className="text-right font-semibold text-[#94a3b8]">{holding.net_quantity}</TableCell><TableCell className="text-center"><button className="lens-btn" onClick={() => viewTransactions(holding)}><Search className="h-4 w-4" /></button></TableCell></TableRow>))}
                        {clientRedeemed.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-8" style={{ color: colors.text_secondary }}>No redeemed holdings</TableCell></TableRow>}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </>
        )}
      </main>
      <Dialog open={showTransactions} onOpenChange={setShowTransactions}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading" style={{ color: colors.primary }}>Transaction Details</DialogTitle>{selectedHolding && <p className="text-sm" style={{ color: colors.text_secondary }}>{selectedHolding.bond_name} ({selectedHolding.bond_isin})</p>}</DialogHeader>
          <div className="space-y-6 mt-4">
            <div><h3 className="font-semibold mb-3 flex items-center gap-2" style={{ color: colors.primary }}><TrendingUp className="h-4 w-4" />Investment Transactions</h3>
              <Table><TableHeader><TableRow style={{ backgroundColor: `${colors.light_bg}80` }}><TableHead style={{ color: colors.primary }}>Date</TableHead><TableHead style={{ color: colors.primary }}>ID</TableHead><TableHead className="text-right" style={{ color: colors.primary }}>Qty</TableHead><TableHead className="text-right" style={{ color: colors.primary }}>Invested</TableHead><TableHead className="text-right" style={{ color: colors.primary }}>Yield</TableHead></TableRow></TableHeader>
              <TableBody>{transactions.investments.map((txn) => (<TableRow key={txn.transaction_id}><TableCell>{formatDate(txn.transaction_date)}</TableCell><TableCell className="font-mono text-xs">{txn.transaction_id}</TableCell><TableCell className="text-right">{txn.quantity}</TableCell><TableCell className="text-right font-semibold">{formatCurrency(txn.invested_value)}</TableCell><TableCell className="text-right" style={{ color: colors.accent }}>{formatPercent(txn.yield_rate)}</TableCell></TableRow>))}{transactions.investments.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-4" style={{ color: colors.text_secondary }}>No investment transactions</TableCell></TableRow>}</TableBody></Table></div>
            <div><div className="flex items-center justify-between mb-3"><h3 className="font-semibold flex items-center gap-2" style={{ color: colors.accent }}><Download className="h-4 w-4" />Redemption Transactions</h3>
              {hasEligibleTxnForRedeem() && (<Button size="sm" className="text-white" style={{ backgroundColor: colors.accent }} onClick={() => handlePlaceRedeemRequest(selectedHolding)} data-testid="partner-place-redeem-request-btn">Place Redeem Request</Button>)}
              </div>
              <Table><TableHeader><TableRow style={{ backgroundColor: `${colors.accent}15` }}><TableHead style={{ color: colors.accent }}>Date</TableHead><TableHead style={{ color: colors.accent }}>ID</TableHead><TableHead className="text-right" style={{ color: colors.accent }}>Qty</TableHead><TableHead className="text-right" style={{ color: colors.accent }}>Redemption Value</TableHead><TableHead className="text-right" style={{ color: colors.accent }}>Yield</TableHead></TableRow></TableHeader>
              <TableBody>{transactions.redemptions.map((txn) => (<TableRow key={txn.transaction_id}><TableCell>{formatDate(txn.transaction_date)}</TableCell><TableCell className="font-mono text-xs">{txn.transaction_id}</TableCell><TableCell className="text-right">{txn.quantity}</TableCell><TableCell className="text-right font-semibold">{formatCurrency(txn.redemption_value)}</TableCell><TableCell className="text-right" style={{ color: colors.accent }}>{formatPercent(txn.yield_rate)}</TableCell></TableRow>))}{transactions.redemptions.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-4" style={{ color: colors.text_secondary }}>No redemption transactions</TableCell></TableRow>}</TableBody></Table></div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Add Client Dialog */}
      <Dialog open={showAddClient} onOpenChange={setShowAddClient}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading" style={{ color: colors.primary }}>Add New Client</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Client Name <span className="text-red-500">*</span></Label>
              <Input value={newClient.client_name} onChange={(e) => setNewClient(prev => ({ ...prev, client_name: e.target.value }))} placeholder="Enter client name" data-testid="partner-new-client-name" />
            </div>
            <div>
              <Label>Email <span className="text-xs text-gray-400 font-normal">(optional)</span></Label>
              <Input type="email" value={newClient.email} onChange={(e) => setNewClient(prev => ({ ...prev, email: e.target.value }))} placeholder="example@domain.com" data-testid="partner-new-client-email" />
              {newClient.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newClient.email) && <p className="text-xs text-red-500 mt-1">Please enter a valid email address</p>}
            </div>
            <div>
              <Label>Mobile <span className="text-xs text-gray-400 font-normal">(optional)</span></Label>
              <Input type="tel" value={newClient.mobile} onChange={(e) => setNewClient(prev => ({ ...prev, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) }))} placeholder="10-digit mobile number" maxLength={10} data-testid="partner-new-client-mobile" />
              {newClient.mobile && newClient.mobile.length > 0 && newClient.mobile.length !== 10 && <p className="text-xs text-red-500 mt-1">Mobile number must be 10 digits</p>}
            </div>
            <p className="text-xs" style={{ color: colors.text_secondary }}>Client ID will be auto-generated by the system.</p>
            {partnerConfig.allow_subpartner && user?.role === 'partner' && (
            <div>
              <Label>Assign to SubPartner <span className="text-xs text-gray-400 font-normal">(optional)</span></Label>
              <Select value={newClient.subpartner_code} onValueChange={(v) => setNewClient(prev => ({ ...prev, subpartner_code: v === '_none_' ? '' : v }))}>
                <SelectTrigger data-testid="partner-new-client-subpartner"><SelectValue placeholder="None (Direct Client)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none_">None (Direct Client)</SelectItem>
                  {subpartners.map(sp => <SelectItem key={sp.subpartner_code} value={sp.subpartner_code}>{sp.subpartner_name} ({sp.subpartner_code})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            )}
            <div>
              <Label>CMR (PDF) <span className="text-xs text-red-500 font-normal">*required</span></Label>
              <Input type="file" accept=".pdf" onChange={(e) => setCmrFile(e.target.files?.[0] || null)} data-testid="partner-new-client-cmr" />
              {!cmrFile && <p className="text-xs text-gray-500 mt-1">Upload Client Master Record (PDF only)</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddClient(false); setCmrFile(null); }}>Cancel</Button>
            <Button style={{ backgroundColor: colors.primary }} className="text-white" onClick={handleAddClient} disabled={savingClient || !newClient.client_name.trim() || !cmrFile} data-testid="partner-save-client-btn">
              {savingClient ? <span className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full inline-block" /> : <Plus className="h-4 w-4 mr-2" />}
              Add Client
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Edit Client Dialog */}
      <Dialog open={showEditClient} onOpenChange={setShowEditClient}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading" style={{ color: colors.primary }}>Edit Client</DialogTitle>
            <p className="text-xs" style={{ color: colors.text_secondary }}>ID: {editClient.client_id}</p>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Client Name <span className="text-red-500">*</span></Label>
              <Input value={editClient.client_name} onChange={(e) => setEditClient(prev => ({ ...prev, client_name: e.target.value }))} disabled={editClient.hasInvestments} data-testid="partner-edit-client-name" />
              {editClient.hasInvestments && <p className="text-xs text-amber-600 mt-1">Name cannot be edited as this client has investment transactions</p>}
            </div>
            <div>
              <Label>Email <span className="text-xs text-gray-400 font-normal">(optional)</span></Label>
              <Input type="email" value={editClient.email} onChange={(e) => setEditClient(prev => ({ ...prev, email: e.target.value }))} placeholder="example@domain.com" data-testid="partner-edit-client-email" />
              {editClient.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editClient.email) && <p className="text-xs text-red-500 mt-1">Please enter a valid email address</p>}
            </div>
            <div>
              <Label>Mobile <span className="text-xs text-gray-400 font-normal">(optional)</span></Label>
              <Input type="tel" value={editClient.mobile} onChange={(e) => setEditClient(prev => ({ ...prev, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) }))} placeholder="10-digit mobile number" maxLength={10} data-testid="partner-edit-client-mobile" />
              {editClient.mobile && editClient.mobile.length > 0 && editClient.mobile.length !== 10 && <p className="text-xs text-red-500 mt-1">Mobile number must be 10 digits</p>}
            </div>
            {partnerConfig.allow_subpartner && user?.role === 'partner' && (
            <div>
              <Label>Assign to SubPartner <span className="text-xs text-gray-400 font-normal">(optional)</span></Label>
              <Select value={editClient.subpartner_code || '_none_'} onValueChange={(v) => setEditClient(prev => ({ ...prev, subpartner_code: v === '_none_' ? '' : v }))}>
                <SelectTrigger data-testid="partner-edit-client-subpartner"><SelectValue placeholder="None (Direct Client)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none_">None (Direct Client)</SelectItem>
                  {subpartners.map(sp => <SelectItem key={sp.subpartner_code} value={sp.subpartner_code}>{sp.subpartner_name} ({sp.subpartner_code})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            )}
            <div>
              <Label>CMR (PDF)</Label>
              {editClient.cmr_path ? (
                <p className="text-xs text-green-600 py-2">CMR already uploaded: {editClient.cmr_filename || 'cmr.pdf'} (Only Admin can replace)</p>
              ) : (
                <>
                  <Input type="file" accept=".pdf" onChange={(e) => setCmrFile(e.target.files?.[0] || null)} data-testid="partner-edit-client-cmr" />
                  <p className="text-xs text-gray-500 mt-1">Upload Client Master Record (PDF only)</p>
                </>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditClient(false); setCmrFile(null); }}>Cancel</Button>
            <Button style={{ backgroundColor: colors.primary }} className="text-white" onClick={handleEditClient} disabled={savingClient || !editClient.client_name.trim()} data-testid="partner-update-client-btn">
              {savingClient ? <span className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full inline-block" /> : <Pencil className="h-4 w-4 mr-2" />}
              Update Client
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Add User Login Dialog */}
      <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading" style={{ color: colors.primary }}>Add User Login</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {partnerConfig.allow_subpartner && user?.role === 'partner' && (
            <div>
              <Label>Login Type <span className="text-red-500">*</span></Label>
              <Select value={newUser.role} onValueChange={(v) => setNewUser(prev => ({ ...prev, role: v, entity_id: '' }))}>
                <SelectTrigger data-testid="partner-new-user-role"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="client">Client Login</SelectItem>
                  <SelectItem value="subpartner">SubPartner Login</SelectItem>
                </SelectContent>
              </Select>
            </div>
            )}
            <div>
              <Label>Username <span className="text-red-500">*</span></Label>
              <Input value={newUser.username} onChange={(e) => setNewUser(prev => ({ ...prev, username: e.target.value }))} placeholder="Enter username" data-testid="partner-new-user-username" />
            </div>
            <div>
              <Label>Password <span className="text-red-500">*</span></Label>
              <Input type="password" value={newUser.password} onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))} placeholder="Minimum 4 characters" data-testid="partner-new-user-password" />
              {newUser.password && newUser.password.length > 0 && newUser.password.length < 4 && <p className="text-xs text-red-500 mt-1">Password must be at least 4 characters</p>}
            </div>
            <div>
              <Label>{newUser.role === 'subpartner' ? 'SubPartner' : 'Client'} <span className="text-red-500">*</span></Label>
              <Select value={newUser.entity_id} onValueChange={(v) => setNewUser(prev => ({ ...prev, entity_id: v }))}>
                <SelectTrigger data-testid="partner-new-user-client"><SelectValue placeholder={newUser.role === 'subpartner' ? 'Select a SubPartner' : 'Select a client'} /></SelectTrigger>
                <SelectContent>
                  {newUser.role === 'subpartner' ? (
                    subpartners.map(sp => <SelectItem key={sp.subpartner_code} value={sp.subpartner_code}>{sp.subpartner_name} ({sp.subpartner_code})</SelectItem>)
                  ) : (
                    clients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.client_name} ({c.client_id})</SelectItem>)
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddUser(false)}>Cancel</Button>
            <Button style={{ backgroundColor: colors.primary }} className="text-white" onClick={handleAddUser} disabled={savingUser || !newUser.username.trim() || !newUser.password || !newUser.entity_id} data-testid="partner-save-user-btn">
              {savingUser ? <span className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full inline-block" /> : <Plus className="h-4 w-4 mr-2" />}
              Create Login
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Edit User Login Dialog */}
      <Dialog open={showEditUser} onOpenChange={setShowEditUser}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading" style={{ color: colors.primary }}>Edit User Login</DialogTitle>
            {editUser.entity_name && <p className="text-xs" style={{ color: colors.text_secondary }}>{editUser.is_self ? 'Partner' : 'Client'}: {editUser.entity_name}</p>}
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Username {!editUser.is_self && <span className="text-red-500">*</span>}</Label>
              <Input value={editUser.username} onChange={(e) => setEditUser(prev => ({ ...prev, username: e.target.value }))} disabled={editUser.is_self} data-testid="partner-edit-user-username" />
              {editUser.is_self && <p className="text-xs mt-1" style={{ color: colors.text_secondary }}>Username cannot be changed for your own account</p>}
            </div>
            <div>
              <Label>New Password <span className="text-xs text-gray-400 font-normal">(leave blank to keep unchanged)</span></Label>
              <Input type="password" value={editUser.password} onChange={(e) => setEditUser(prev => ({ ...prev, password: e.target.value }))} placeholder="Enter new password" data-testid="partner-edit-user-password" />
              {editUser.password && editUser.password.length > 0 && editUser.password.length < 4 && <p className="text-xs text-red-500 mt-1">Password must be at least 4 characters</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditUser(false)}>Cancel</Button>
            <Button style={{ backgroundColor: colors.primary }} className="text-white" onClick={handleEditUser} disabled={savingUser || !editUser.username.trim()} data-testid="partner-update-user-btn">
              {savingUser ? <span className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full inline-block" /> : <Pencil className="h-4 w-4 mr-2" />}
              Update Login
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Add/Edit Investment Dialog */}
      <Dialog open={showInvModal} onOpenChange={setShowInvModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading" style={{ color: colors.primary }}>{editingInv ? 'Edit' : 'Add'} Investment</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Transaction Date <span className="text-red-500">*</span></Label>
              <Input type="date" value={invForm.transaction_date} onChange={(e) => setInvForm(prev => ({ ...prev, transaction_date: e.target.value }))} data-testid="inv-txn-date" />
            </div>
            <div>
              <Label>Client Name <span className="text-red-500">*</span></Label>
              <Select value={invForm.client_name} onValueChange={(v) => setInvForm(prev => ({ ...prev, client_name: v }))}>
                <SelectTrigger data-testid="inv-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>
                  {[...new Set(clients.map(c => c.client_name))].sort().map(name => <SelectItem key={name} value={name}>{name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Bond ISIN <span className="text-red-500">*</span></Label>
              <Input value={invForm.bond_isin} onChange={(e) => setInvForm(prev => ({ ...prev, bond_isin: e.target.value }))} placeholder="e.g. INE001A07XXX" data-testid="inv-bond-isin" />
            </div>
            <div>
              <Label>Bond Name <span className="text-red-500">*</span></Label>
              <Input value={invForm.bond_name} onChange={(e) => setInvForm(prev => ({ ...prev, bond_name: e.target.value }))} placeholder="Bond name" data-testid="inv-bond-name" />
            </div>
            <div>
              <Label>Quantity <span className="text-red-500">*</span></Label>
              <Input type="number" value={invForm.quantity} onChange={(e) => setInvForm(prev => ({ ...prev, quantity: e.target.value }))} placeholder="Qty" data-testid="inv-quantity" />
            </div>
            <div>
              <Label>Face Value <span className="text-red-500">*</span></Label>
              <Input type="number" value={invForm.face_value} onChange={(e) => setInvForm(prev => ({ ...prev, face_value: e.target.value }))} placeholder="Face value" data-testid="inv-face-value" />
            </div>
            <div>
              <Label>Invested Value <span className="text-red-500">*</span></Label>
              <Input type="number" value={invForm.invested_value} onChange={(e) => setInvForm(prev => ({ ...prev, invested_value: e.target.value }))} placeholder="Invested value" data-testid="inv-invested-value" />
            </div>
            <div>
              <Label>Yield (%) <span className="text-red-500">*</span></Label>
              <Input type="number" step="0.01" value={invForm.yield_rate} onChange={(e) => setInvForm(prev => ({ ...prev, yield_rate: e.target.value }))} placeholder="e.g. 9 for 9%" data-testid="inv-yield" />
              <p className="text-xs mt-0.5" style={{ color: colors.text_secondary }}>Enter as percentage (e.g. 9 for 9%)</p>
            </div>
            <div>
              <Label>Maturity Date <span className="text-red-500">*</span></Label>
              <Input type="date" value={invForm.maturity_date} onChange={(e) => setInvForm(prev => ({ ...prev, maturity_date: e.target.value }))} data-testid="inv-maturity" />
            </div>
            <div>
              <Label>1st Maturity %</Label>
              <Input type="number" min="0" max="100" value={invForm.maturity_pct_1 ?? '100'} onChange={(e) => setInvForm(prev => ({ ...prev, maturity_pct_1: e.target.value }))} placeholder="100" data-testid="inv-maturity-pct-1" />
            </div>
          </div>
          {Number(invForm.maturity_pct_1 || 100) < 100 && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>2nd Maturity Date</Label>
                <Input type="date" value={invForm.maturity_date_2 || ''} onChange={(e) => setInvForm(prev => ({ ...prev, maturity_date_2: e.target.value }))} data-testid="inv-maturity-date-2" />
              </div>
              <div>
                <Label>2nd Maturity %</Label>
                <Input type="number" min="0" max="100" value={invForm.maturity_pct_2 || ''} onChange={(e) => setInvForm(prev => ({ ...prev, maturity_pct_2: e.target.value }))} data-testid="inv-maturity-pct-2" />
              </div>
              <div>
                <Label>3rd Maturity Date</Label>
                <Input type="date" value={invForm.maturity_date_3 || ''} onChange={(e) => setInvForm(prev => ({ ...prev, maturity_date_3: e.target.value }))} data-testid="inv-maturity-date-3" />
              </div>
              <div>
                <Label>3rd Maturity %</Label>
                <Input type="number" min="0" max="100" value={invForm.maturity_pct_3 || ''} onChange={(e) => setInvForm(prev => ({ ...prev, maturity_pct_3: e.target.value }))} data-testid="inv-maturity-pct-3" />
              </div>
            </div>
          )}
          {Number(invForm.maturity_pct_1 || 100) < 100 && (() => { const t = (Number(invForm.maturity_pct_1) || 0) + (Number(invForm.maturity_pct_2) || 0) + (Number(invForm.maturity_pct_3) || 0); return <p className={`text-xs font-medium px-2 py-1 rounded mb-2 ${t === 100 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`} data-testid="maturity-pct-total">Maturity % Total: {t}% {t === 100 ? '(Valid)' : '(Must equal 100%)'}</p>; })()}
          {partnerConfig.allow_my_bond_commission && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-1 block text-sm font-medium">Commission Type</Label>
                <Select value={invForm.commn_type || 'Trail'} onValueChange={(v) => setInvForm(prev => ({ ...prev, commn_type: v }))}>
                  <SelectTrigger data-testid="inv-commn-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Trail">Trail</SelectItem>
                    <SelectItem value="OneTime">OneTime</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1 block text-sm font-medium">Partner Share (%)</Label>
                <Input type="number" step="0.01" min="0" max="100" placeholder="e.g. 0.50" value={invForm.partner_share} onChange={(e) => setInvForm(prev => ({ ...prev, partner_share: e.target.value }))} data-testid="inv-partner-share" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvModal(false)}>Cancel</Button>
            <Button style={{ backgroundColor: colors.primary }} className="text-white" onClick={handleSaveInv} disabled={savingInv} data-testid="save-inv-btn">
              {savingInv ? <span className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full inline-block" /> : <Plus className="h-4 w-4 mr-2" />}
              {editingInv ? 'Update' : 'Add'} Investment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Add/Edit Redemption Dialog */}
      <Dialog open={showRedModal} onOpenChange={setShowRedModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading" style={{ color: colors.accent }}>{editingRed ? 'Edit' : 'Add'} Redemption</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!editingRed && (
              <>
              <div>
                <Label>Client <span className="text-red-500">*</span></Label>
                <Select value={redClientId} onValueChange={(v) => { setRedClientId(v); setRedForm(prev => ({ ...prev, investment_txn_id: '' })); fetchInvestmentOptions(v); }}>
                  <SelectTrigger data-testid="red-client-select"><SelectValue placeholder="Select client first" /></SelectTrigger>
                  <SelectContent>
                    {clients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.client_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Investment Transaction <span className="text-red-500">*</span></Label>
                <Select value={redForm.investment_txn_id} onValueChange={(v) => setRedForm(prev => ({ ...prev, investment_txn_id: v }))} disabled={!redClientId}>
                  <SelectTrigger data-testid="red-inv-select"><SelectValue placeholder={redClientId ? 'Select investment transaction' : 'Select a client first'} /></SelectTrigger>
                  <SelectContent>
                    {investmentOptions.map(inv => <SelectItem key={inv.transaction_id} value={inv.transaction_id}>{inv.transaction_date?.split(' ')[0] || inv.transaction_date} - {inv.bond_isin} - {inv.bond_name} (Qty: {inv.quantity})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              </>
            )}
            {editingRed && (
              <div>
                <Label>Transaction</Label>
                <p className="text-sm font-medium" style={{ color: colors.text_secondary }}>{editingRed.transaction_id} - {editingRed.bond_isin} - {editingRed.bond_name}</p>
              </div>
            )}
            <div>
              <Label>Transaction Date <span className="text-red-500">*</span></Label>
              <Input type="date" value={redForm.transaction_date} onChange={(e) => setRedForm(prev => ({ ...prev, transaction_date: e.target.value }))} data-testid="red-txn-date" />
            </div>
            <div>
              <Label>Quantity <span className="text-red-500">*</span></Label>
              <Input type="number" value={redForm.quantity} onChange={(e) => setRedForm(prev => ({ ...prev, quantity: e.target.value }))} placeholder="Qty" data-testid="red-quantity" />
            </div>
            <div>
              <Label>Redemption Value <span className="text-red-500">*</span></Label>
              <Input type="number" value={redForm.redemption_value} onChange={(e) => setRedForm(prev => ({ ...prev, redemption_value: e.target.value }))} placeholder="Redemption value" data-testid="red-value" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRedModal(false)}>Cancel</Button>
            <Button style={{ backgroundColor: colors.accent }} className="text-white" onClick={handleSaveRed} disabled={savingRed} data-testid="save-red-btn">
              {savingRed ? <span className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full inline-block" /> : <Plus className="h-4 w-4 mr-2" />}
              {editingRed ? 'Update' : 'Add'} Redemption
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Partner Redeem Request Dialog */}
      <Dialog open={showRedeemDialog} onOpenChange={setShowRedeemDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2" style={{ color: colors.accent }}>
              Place Redeem Request
            </DialogTitle>
            {redeemBond && <p className="text-sm mt-1" style={{ color: colors.text_secondary }}>{redeemBond.bond_name}</p>}
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label>Please enter Qty</Label>
              <Input type="number" value={redeemQty} onChange={(e) => setRedeemQty(e.target.value)} placeholder="Enter quantity" className="mt-1" data-testid="partner-redeem-qty-input" autoFocus />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRedeemDialog(false)}>Cancel</Button>
            <Button className="text-white" style={{ backgroundColor: colors.accent }} onClick={handleSubmitRedeem} disabled={sendingRedeem || !redeemQty} data-testid="partner-redeem-submit-btn">
              {sendingRedeem ? (
                <><span className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full inline-block" />Sending request...</>
              ) : (
                <><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="white" className="mr-2"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>Submit Request</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* WhatsApp Enquiry Dialog */}
      <Dialog open={showEnquiryDialog} onOpenChange={setShowEnquiryDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2" style={{ color: colors.primary }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              Enquire via WhatsApp
            </DialogTitle>
            {enquiryBond && <p className="text-sm mt-1" style={{ color: colors.text_secondary }}>{enquiryBond.bond_name}</p>}
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label>Please enter Investment amount</Label>
              <Input type="number" value={enquiryAmount} onChange={(e) => setEnquiryAmount(e.target.value)} placeholder="Enter amount in INR" className="mt-1" data-testid="enquiry-amount-input" autoFocus />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEnquiryDialog(false)}>Cancel</Button>
            <Button className="text-white" style={{ backgroundColor: '#25D366' }} onClick={handleEnquiry} disabled={sendingEnquiry || !enquiryAmount} data-testid="enquiry-submit-btn">
              {sendingEnquiry ? (
                <><span className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full inline-block" />Sending enquiry...</>
              ) : (
                <><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="white" className="mr-2"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>Send Enquiry</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Bond Image Preview Dialog */}
      <Dialog open={showBondImagePreview} onOpenChange={setShowBondImagePreview}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading" style={{ color: colors.primary }}>Bond Details Image</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4">
            <img src={previewBondImage} alt="Bond Details" className="max-w-full max-h-[55vh] object-contain rounded-lg border" data-testid="partner-bond-image-preview" />
            <a href={previewBondImage} download="bond_details.png" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" style={{ borderColor: colors.primary, color: colors.primary }} data-testid="partner-download-bond-image-btn"><Download className="h-4 w-4 mr-2" />Download Image</Button>
            </a>
          </div>
        </DialogContent>
      </Dialog>
      {/* Add SubPartner Dialog */}
      <Dialog open={showAddSubpartner} onOpenChange={setShowAddSubpartner}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading" style={{ color: colors.primary }}>Add SubPartner</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>SubPartner Name <span className="text-red-500">*</span></Label>
              <Input value={subpartnerForm.subpartner_name} onChange={(e) => setSubpartnerForm(prev => ({ ...prev, subpartner_name: e.target.value }))} placeholder="Enter name" data-testid="sp-name-input" />
            </div>
            <div>
              <Label>Email <span className="text-xs text-gray-400 font-normal">(optional)</span></Label>
              <Input type="email" value={subpartnerForm.email} onChange={(e) => setSubpartnerForm(prev => ({ ...prev, email: e.target.value }))} placeholder="example@domain.com" data-testid="sp-email-input" />
            </div>
            <div>
              <Label>Mobile <span className="text-xs text-gray-400 font-normal">(optional)</span></Label>
              <Input type="tel" value={subpartnerForm.mobile} onChange={(e) => setSubpartnerForm(prev => ({ ...prev, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) }))} placeholder="10-digit mobile" maxLength={10} data-testid="sp-mobile-input" />
            </div>
            <div>
              <Label>SubPartner Share (%) <span className="text-red-500">*</span></Label>
              <Input type="number" min="0" max="100" step="0.01" value={subpartnerForm.subpartner_share} onChange={(e) => setSubpartnerForm(prev => ({ ...prev, subpartner_share: e.target.value }))} placeholder="e.g., 30" data-testid="sp-share-input" />
              <p className="text-xs mt-1" style={{ color: colors.text_secondary }}>Percentage of partner commission allocated to this SubPartner</p>
            </div>
            <p className="text-xs" style={{ color: colors.text_secondary }}>SubPartner Code will be auto-generated.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddSubpartner(false)}>Cancel</Button>
            <Button style={{ backgroundColor: colors.primary }} className="text-white" onClick={() => handleSaveSubpartner(false)} disabled={savingSubpartner || !subpartnerForm.subpartner_name.trim()} data-testid="sp-save-btn">
              {savingSubpartner ? <span className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full inline-block" /> : <Plus className="h-4 w-4 mr-2" />}
              Add SubPartner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Edit SubPartner Dialog */}
      <Dialog open={showEditSubpartner} onOpenChange={setShowEditSubpartner}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading" style={{ color: colors.primary }}>Edit SubPartner</DialogTitle>
            <p className="text-xs" style={{ color: colors.text_secondary }}>Code: {editingSubpartnerCode}</p>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>SubPartner Name <span className="text-red-500">*</span></Label>
              <Input value={subpartnerForm.subpartner_name} onChange={(e) => setSubpartnerForm(prev => ({ ...prev, subpartner_name: e.target.value }))} data-testid="sp-edit-name-input" />
            </div>
            <div>
              <Label>Email <span className="text-xs text-gray-400 font-normal">(optional)</span></Label>
              <Input type="email" value={subpartnerForm.email} onChange={(e) => setSubpartnerForm(prev => ({ ...prev, email: e.target.value }))} data-testid="sp-edit-email-input" />
            </div>
            <div>
              <Label>Mobile <span className="text-xs text-gray-400 font-normal">(optional)</span></Label>
              <Input type="tel" value={subpartnerForm.mobile} onChange={(e) => setSubpartnerForm(prev => ({ ...prev, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) }))} maxLength={10} data-testid="sp-edit-mobile-input" />
            </div>
            <div>
              <Label>SubPartner Share (%) <span className="text-red-500">*</span></Label>
              <Input type="number" min="0" max="100" step="0.01" value={subpartnerForm.subpartner_share} onChange={(e) => setSubpartnerForm(prev => ({ ...prev, subpartner_share: e.target.value }))} data-testid="sp-edit-share-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditSubpartner(false)}>Cancel</Button>
            <Button style={{ backgroundColor: colors.primary }} className="text-white" onClick={() => handleSaveSubpartner(true)} disabled={savingSubpartner || !subpartnerForm.subpartner_name.trim()} data-testid="sp-update-btn">
              {savingSubpartner ? <span className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full inline-block" /> : <Pencil className="h-4 w-4 mr-2" />}
              Update SubPartner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* My Bond Add/Edit Dialog */}
      <Dialog open={showMyBondModal} onOpenChange={setShowMyBondModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading" style={{ color: colors.primary }}>{editingMyBond ? 'Edit Bond' : 'Add Bond'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Bond ISIN <span className="text-red-500">*</span></Label>
                <Input value={myBondForm.bond_isin} onChange={(e) => setMyBondForm(prev => ({ ...prev, bond_isin: e.target.value }))} placeholder="e.g., INE001A07XX" disabled={!!editingMyBond} data-testid="mybond-isin-input" />
              </div>
              <div>
                <Label>Bond Name <span className="text-red-500">*</span></Label>
                <Input value={myBondForm.bond_name} onChange={(e) => setMyBondForm(prev => ({ ...prev, bond_name: e.target.value }))} placeholder="Bond name" data-testid="mybond-name-input" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Rating</Label>
                <Input value={myBondForm.rating} onChange={(e) => setMyBondForm(prev => ({ ...prev, rating: e.target.value }))} placeholder="e.g., AAA" data-testid="mybond-rating-input" />
              </div>
              <div>
                <Label>Yield (%)</Label>
                <Input type="number" step="0.01" value={myBondForm.yield_rate} onChange={(e) => setMyBondForm(prev => ({ ...prev, yield_rate: e.target.value }))} placeholder="e.g., 8.5" data-testid="mybond-yield-input" />
              </div>
              <div>
                <Label>Maturity Date</Label>
                <Input type="date" value={myBondForm.maturity_date} onChange={(e) => setMyBondForm(prev => ({ ...prev, maturity_date: e.target.value }))} data-testid="mybond-maturity-input" />
              </div>
              <div>
                <Label>1st Maturity %</Label>
                <Input type="number" min="0" max="100" value={myBondForm.maturity_pct_1 ?? '100'} onChange={(e) => setMyBondForm(prev => ({ ...prev, maturity_pct_1: e.target.value }))} placeholder="100" data-testid="mybond-maturity-pct-1" />
              </div>
            </div>
            {Number(myBondForm.maturity_pct_1 || 100) < 100 && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>2nd Maturity Date</Label>
                <Input type="date" value={myBondForm.maturity_date_2 || ''} onChange={(e) => setMyBondForm(prev => ({ ...prev, maturity_date_2: e.target.value }))} data-testid="mybond-maturity-date-2" />
              </div>
              <div>
                <Label>2nd Maturity %</Label>
                <Input type="number" min="0" max="100" value={myBondForm.maturity_pct_2 || ''} onChange={(e) => setMyBondForm(prev => ({ ...prev, maturity_pct_2: e.target.value }))} data-testid="mybond-maturity-pct-2" />
              </div>
              <div>
                <Label>3rd Maturity Date</Label>
                <Input type="date" value={myBondForm.maturity_date_3 || ''} onChange={(e) => setMyBondForm(prev => ({ ...prev, maturity_date_3: e.target.value }))} data-testid="mybond-maturity-date-3" />
              </div>
              <div>
                <Label>3rd Maturity %</Label>
                <Input type="number" min="0" max="100" value={myBondForm.maturity_pct_3 || ''} onChange={(e) => setMyBondForm(prev => ({ ...prev, maturity_pct_3: e.target.value }))} data-testid="mybond-maturity-pct-3" />
              </div>
              {(() => { const t = (Number(myBondForm.maturity_pct_1) || 0) + (Number(myBondForm.maturity_pct_2) || 0) + (Number(myBondForm.maturity_pct_3) || 0); return <div className="col-span-2"><p className={`text-xs font-medium px-2 py-1 rounded ${t === 100 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`} data-testid="mybond-maturity-pct-total">Maturity % Total: {t}% {t === 100 ? '(Valid)' : '(Must equal 100%)'}</p></div>; })()}
            </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Payout Frequency</Label>
                <Select value={myBondForm.interest_payout_frequency} onValueChange={(v) => setMyBondForm(prev => ({ ...prev, interest_payout_frequency: v }))}>
                  <SelectTrigger data-testid="mybond-freq-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Monthly">Monthly</SelectItem>
                    <SelectItem value="Quarterly">Quarterly</SelectItem>
                    <SelectItem value="Semi-Annual">Semi-Annual</SelectItem>
                    <SelectItem value="Annual">Annual</SelectItem>
                    <SelectItem value="Cumulative">Cumulative</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-3 pt-6">
                <Label>Available</Label>
                <Button variant={myBondForm.available ? 'default' : 'outline'} size="sm" className={myBondForm.available ? 'bg-green-600 hover:bg-green-700' : ''} onClick={() => setMyBondForm(prev => ({ ...prev, available: !prev.available }))} data-testid="mybond-available-toggle">
                  {myBondForm.available ? 'Yes' : 'No'}
                </Button>
              </div>
            </div>
            <div>
              <Label>Bond Image</Label>
              <div className="flex gap-2 items-center">
                <Input value={myBondForm.bond_image_url} onChange={(e) => setMyBondForm(prev => ({ ...prev, bond_image_url: e.target.value }))} placeholder="Image URL or upload" className="flex-1" data-testid="mybond-image-url" />
                <label className="cursor-pointer">
                  <input type="file" accept="image/*" className="hidden" onChange={handleMyBondImageUpload} />
                  <span className="inline-flex items-center gap-1 px-3 py-2 rounded-md border text-sm hover:bg-gray-50"><ImageIcon className="h-4 w-4" />Upload</span>
                </label>
              </div>
              {myBondForm.bond_image_url && (
                <div className="mt-2 relative w-32 h-20 border rounded overflow-hidden">
                  <img src={myBondForm.bond_image_url} alt="Preview" className="w-full h-full object-contain" />
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMyBondModal(false)}>Cancel</Button>
            <Button style={{ backgroundColor: colors.primary }} className="text-white" onClick={handleSaveMyBond} disabled={savingMyBond || !myBondForm.bond_isin.trim() || !myBondForm.bond_name.trim()} data-testid="mybond-save-btn">
              {savingMyBond ? <span className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full inline-block" /> : editingMyBond ? <Pencil className="h-4 w-4 mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              {editingMyBond ? 'Update Bond' : 'Add Bond'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Family Add/Edit Modal */}
      <Dialog open={showFamilyModal} onOpenChange={setShowFamilyModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading" style={{ color: colors.primary }}>{editingFamily ? 'Edit' : 'Add'} Family</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Family Name</Label><Input value={familyForm.family_name} onChange={(e) => setFamilyForm(prev => ({ ...prev, family_name: e.target.value }))} placeholder="Enter family name" data-testid="partner-family-name-input" /></div>
            {!editingFamily && (
              <div><Label>Family Head (Client)</Label><Select value={familyForm.head_client_id} onValueChange={(v) => setFamilyForm(prev => ({ ...prev, head_client_id: v, member_client_ids: prev.member_client_ids.filter(id => id !== v) }))}><SelectTrigger data-testid="partner-family-head-select"><SelectValue placeholder="Select Head Client" /></SelectTrigger><SelectContent>{clients.map(c => (<SelectItem key={c.client_id} value={c.client_id}>{c.client_name} ({c.client_id})</SelectItem>))}</SelectContent></Select></div>
            )}
            {editingFamily && (
              <div><Label>Family Head</Label><Input value={`${editingFamily.head_client_name} (${editingFamily.head_client_id})`} disabled className="bg-gray-50" /></div>
            )}
            <div>
              <Label>Family Members</Label>
              <p className="text-xs text-gray-500 mb-2">Head is automatically included. Select additional members below.</p>
              <div className="max-h-48 overflow-y-auto border rounded-md p-2 space-y-1">
                {clients.filter(c => c.client_id !== familyForm.head_client_id).map(c => (
                  <label key={c.client_id} className="flex items-center gap-2 p-1.5 hover:bg-gray-50 rounded cursor-pointer">
                    <input type="checkbox" className="rounded" checked={familyForm.member_client_ids.includes(c.client_id)} onChange={(e) => {
                      setFamilyForm(prev => ({
                        ...prev,
                        member_client_ids: e.target.checked
                          ? [...prev.member_client_ids, c.client_id]
                          : prev.member_client_ids.filter(id => id !== c.client_id)
                      }));
                    }} />
                    <span className="text-sm">{c.client_name} <span className="text-gray-400">({c.client_id})</span></span>
                  </label>
                ))}
                {clients.filter(c => c.client_id !== familyForm.head_client_id).length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-2">No other clients available</p>
                )}
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowFamilyModal(false)}>Cancel</Button>
            <Button style={{ backgroundColor: colors.primary }} className="text-white" data-testid="partner-save-family-btn" onClick={async () => {
              if (!familyForm.family_name.trim()) { toast.error('Family name is required'); return; }
              if (!editingFamily && !familyForm.head_client_id) { toast.error('Select a family head'); return; }
              try {
                if (editingFamily) {
                  await axios.put(`${API_URL}/api/partner/families/${editingFamily.family_id}`, {
                    family_name: familyForm.family_name,
                    member_client_ids: familyForm.member_client_ids
                  }, getAuthHeaders());
                  toast.success('Family updated');
                } else {
                  await axios.post(`${API_URL}/api/partner/families`, {
                    family_name: familyForm.family_name,
                    head_client_id: familyForm.head_client_id,
                    member_client_ids: familyForm.member_client_ids
                  }, getAuthHeaders());
                  toast.success('Family created');
                }
                setShowFamilyModal(false);
                fetchFamilies();
              } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save family'); }
            }}>Save</Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Bond Holdings Transaction Details Dialog */}
      <Dialog open={showBhTxn} onOpenChange={setShowBhTxn}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading" style={{ color: colors.primary }}>Transaction Details</DialogTitle>
            {bhTxnClient && <p className="text-sm" style={{ color: colors.text_secondary }}>{bhTxnClient.client_name} — {bhTxnClient.bond_name} ({bhTxnClient.bond_isin})</p>}
          </DialogHeader>
          <div className="space-y-6 mt-4">
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ color: colors.primary }}><TrendingUp className="h-4 w-4" />Investment Transactions</h3>
              <Table><TableHeader><TableRow style={{ backgroundColor: `${colors.light_bg}80` }}><TableHead style={{ color: colors.primary }}>Date</TableHead><TableHead style={{ color: colors.primary }}>ID</TableHead><TableHead className="text-right" style={{ color: colors.primary }}>Qty</TableHead><TableHead className="text-right" style={{ color: colors.primary }}>Invested</TableHead><TableHead className="text-right" style={{ color: colors.primary }}>Yield</TableHead></TableRow></TableHeader>
              <TableBody>{bhTxnData.investments.map((txn) => (<TableRow key={txn.transaction_id}><TableCell>{formatDate(txn.transaction_date)}</TableCell><TableCell className="font-mono text-xs">{txn.transaction_id}</TableCell><TableCell className="text-right">{txn.quantity}</TableCell><TableCell className="text-right font-semibold">{formatCurrency(txn.invested_value)}</TableCell><TableCell className="text-right" style={{ color: colors.accent }}>{formatPercent(txn.yield_rate)}</TableCell></TableRow>))}{bhTxnData.investments.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-4" style={{ color: colors.text_secondary }}>No investment transactions</TableCell></TableRow>}</TableBody></Table>
            </div>
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ color: colors.accent }}><Download className="h-4 w-4" />Redemption Transactions</h3>
              <Table><TableHeader><TableRow style={{ backgroundColor: `${colors.accent}15` }}><TableHead style={{ color: colors.accent }}>Date</TableHead><TableHead style={{ color: colors.accent }}>ID</TableHead><TableHead className="text-right" style={{ color: colors.accent }}>Qty</TableHead><TableHead className="text-right" style={{ color: colors.accent }}>Redemption Value</TableHead><TableHead className="text-right" style={{ color: colors.accent }}>Yield</TableHead></TableRow></TableHeader>
              <TableBody>{bhTxnData.redemptions.map((txn) => (<TableRow key={txn.transaction_id}><TableCell>{formatDate(txn.transaction_date)}</TableCell><TableCell className="font-mono text-xs">{txn.transaction_id}</TableCell><TableCell className="text-right">{txn.quantity}</TableCell><TableCell className="text-right font-semibold">{formatCurrency(txn.redemption_value)}</TableCell><TableCell className="text-right" style={{ color: colors.accent }}>{formatPercent(txn.yield_rate)}</TableCell></TableRow>))}{bhTxnData.redemptions.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-4" style={{ color: colors.text_secondary }}>No redemption transactions</TableCell></TableRow>}</TableBody></Table>
            </div>
            {(() => { const allIds = [...bhTxnData.investments.map(t => t.transaction_id), ...bhTxnData.redemptions.map(t => t.transaction_id)]; return allIds.some(id => String(id).startsWith('20')); })() && (
              <div className="flex justify-end">
                <Button size="sm" className="text-white" style={{ backgroundColor: colors.accent }} onClick={() => { setRedeemBond({ bond_isin: bhTxnClient?.bond_isin, bond_name: bhTxnClient?.bond_name }); setRedeemQty(''); setShowBhTxn(false); setShowRedeemDialog(true); }} data-testid="bh-place-redeem-btn">Place Redeem Request</Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Password confirmation for destructive actions */}
      <PasswordConfirmModal
        open={pwdConfirm.open}
        onOpenChange={(o) => setPwdConfirm(prev => ({ ...prev, open: o }))}
        title={pwdConfirm.title}
        description={pwdConfirm.description}
        itemLabel={pwdConfirm.itemLabel}
        getAuthHeaders={getAuthHeaders}
        onConfirmed={pwdConfirm.onConfirmed || (() => {})}
      />
    </div>
  );
};

export default PartnerDashboard;